import React, { useState, useEffect, useCallback, useRef } from 'react';
import DesiBitesDash from '../Game/DesiBitesDash';
import './KidsSection.css';

// ═══════════════════════════════════════════════════════════════════
//  UTILITIES
// ═══════════════════════════════════════════════════════════════════

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function getWrongNums(correct, min, max, count) {
  const set = new Set();
  let tries = 0;
  while (set.size < count && tries < 200) {
    const n = Math.floor(Math.random() * (max - min + 1)) + min;
    if (n !== correct) set.add(n);
    tries++;
  }
  return [...set].slice(0, count);
}

function getAgeKey(age) {
  if (age <= 5)  return '3-5';
  if (age <= 7)  return '6-7';
  if (age <= 9)  return '8-9';
  return '10-12';
}

// ═══════════════════════════════════════════════════════════════════
//  AGE GROUP CONFIG
// ═══════════════════════════════════════════════════════════════════

const AGE_GROUPS = [
  { key:'3-5',   label:'Ages 3–5',   grade:'Pre-K',     emoji:'🐣', color:'#ff6b9d', light:'#fff0f7', dark:'#c2185b' },
  { key:'6-7',   label:'Ages 6–7',   grade:'K – 1st',   emoji:'🐥', color:'#ff9500', light:'#fff8f0', dark:'#e65100' },
  { key:'8-9',   label:'Ages 8–9',   grade:'2nd – 3rd', emoji:'🦊', color:'#00c853', light:'#f0fff4', dark:'#1b5e20' },
  { key:'10-12', label:'Ages 10–12', grade:'4th – 6th', emoji:'🦁', color:'#6c63ff', light:'#f3f0ff', dark:'#311b92' },
];

const ACTIVITY_MAP = {
  '3-5': [
    { id:'game',   label:'Desi Bites Dash', emoji:'🎮', color:'#ff6b9d', desc:'Catch yummy food!' },
    { id:'count',  label:'Counting Fun',    emoji:'🔢', color:'#ff9500', desc:'Count with emojis!' },
    { id:'abc',    label:'Letter Land',     emoji:'🔤', color:'#00bcd4', desc:'Learn your ABCs!' },
    { id:'colors', label:'Colors & Shapes', emoji:'🎨', color:'#9c27b0', desc:'What color is it?' },
    { id:'memory', label:'Memory Match',    emoji:'🧩', color:'#4caf50', desc:'Flip and match!' },
    { id:'simon',  label:'Memory Lights',   emoji:'🎵', color:'#e91e63', desc:'Repeat the lights!' },
    { id:'pattern',label:'What Comes Next', emoji:'🔮', color:'#3f51b5', desc:'Finish the pattern!' },
    { id:'oddone', label:'Odd One Out',     emoji:'🔍', color:'#009688', desc:'Spot the odd one!' },
    { id:'doodle', label:'Doodle Pad',      emoji:'🖍️', color:'#ff5252', desc:'Draw anything!' },
    { id:'rewards',label:'My Trophies',     emoji:'🏆', color:'#ffb300', desc:'See your rewards!' },
  ],
  '6-7': [
    { id:'game',   label:'Desi Bites Dash', emoji:'🎮', color:'#ff6b9d', desc:'Catch falling food!' },
    { id:'math',   label:'Math Whiz',       emoji:'➕', color:'#ff5722', desc:'Add & subtract!' },
    { id:'words',  label:'Sight Words',     emoji:'📖', color:'#2196f3', desc:'Fill in the blank!' },
    { id:'skip',   label:'Skip Counting',   emoji:'🔢', color:'#ff9500', desc:'Count by 2s & 5s!' },
    { id:'memory', label:'Memory Match',    emoji:'🧩', color:'#4caf50', desc:'Flip and match!' },
    { id:'simon',  label:'Memory Lights',   emoji:'🎵', color:'#e91e63', desc:'Repeat the lights!' },
    { id:'pattern',label:'What Comes Next', emoji:'🔮', color:'#3f51b5', desc:'Finish the pattern!' },
    { id:'oddone', label:'Odd One Out',     emoji:'🔍', color:'#009688', desc:'Spot the odd one!' },
    { id:'science',label:'Fun Facts',       emoji:'🔬', color:'#00acc1', desc:'Amazing science!' },
    { id:'story',  label:'Story Corner',    emoji:'📚', color:'#795548', desc:'Read a story!' },
    { id:'storymaker', label:'Story Maker', emoji:'✨', color:'#ab47bc', desc:'Build a silly tale!' },
    { id:'doodle', label:'Doodle Pad',      emoji:'🖍️', color:'#ff5252', desc:'Draw anything!' },
    { id:'rewards',label:'My Trophies',     emoji:'🏆', color:'#ffb300', desc:'See your rewards!' },
  ],
  '8-9': [
    { id:'game',     label:'Desi Bites Dash', emoji:'🎮', color:'#ff6b9d', desc:'Catch falling food!' },
    { id:'math',     label:'Math Whiz',       emoji:'✖️', color:'#ff5722', desc:'Multiply & divide!' },
    { id:'spelling', label:'Spelling Bee',    emoji:'🐝', color:'#ffc107', desc:'Spell it right!' },
    { id:'story',    label:'Story Corner',    emoji:'📚', color:'#795548', desc:'Read & answer!' },
    { id:'memory',   label:'Memory Match',    emoji:'🧩', color:'#4caf50', desc:'4×4 challenge!' },
    { id:'word',     label:'Word Builder',    emoji:'🔠', color:'#9c27b0', desc:'Unscramble words!' },
    { id:'simon',    label:'Memory Lights',   emoji:'🎵', color:'#e91e63', desc:'Repeat the lights!' },
    { id:'pattern',  label:'Pattern Pro',     emoji:'🔮', color:'#3f51b5', desc:'Crack the sequence!' },
    { id:'science',  label:'Fun Facts',       emoji:'🔬', color:'#00acc1', desc:'Amazing science!' },
    { id:'geo',      label:'World Explorer',  emoji:'🌍', color:'#43a047', desc:'Flags & capitals!' },
    { id:'storymaker', label:'Story Maker',   emoji:'✨', color:'#ab47bc', desc:'Build a silly tale!' },
    { id:'doodle',   label:'Doodle Pad',      emoji:'🖍️', color:'#ff5252', desc:'Draw anything!' },
    { id:'rewards',  label:'My Trophies',     emoji:'🏆', color:'#ffb300', desc:'See your rewards!' },
  ],
  '10-12': [
    { id:'game',   label:'Desi Bites Dash', emoji:'🎮', color:'#ff6b9d', desc:'Catch falling food!' },
    { id:'math',   label:'Math Whiz',       emoji:'➗', color:'#ff5722', desc:'Fractions & more!' },
    { id:'vocab',  label:'Vocabulary',      emoji:'📝', color:'#2196f3', desc:'Word power!' },
    { id:'story',  label:'Story Corner',    emoji:'📚', color:'#795548', desc:'Read & comprehend!' },
    { id:'memory', label:'Memory Match',    emoji:'🧩', color:'#4caf50', desc:'Expert mode!' },
    { id:'word',   label:'Word Builder',    emoji:'🔠', color:'#9c27b0', desc:'Build words!' },
    { id:'simon',  label:'Memory Lights',   emoji:'🎵', color:'#e91e63', desc:'Repeat the lights!' },
    { id:'pattern',label:'Pattern Pro',     emoji:'🔮', color:'#3f51b5', desc:'Crack the sequence!' },
    { id:'science',label:'Fun Facts',       emoji:'🔬', color:'#00acc1', desc:'Amazing science!' },
    { id:'geo',    label:'World Explorer',  emoji:'🌍', color:'#43a047', desc:'Flags & capitals!' },
    { id:'storymaker', label:'Story Maker', emoji:'✨', color:'#ab47bc', desc:'Build a silly tale!' },
    { id:'doodle', label:'Doodle Pad',      emoji:'🖍️', color:'#ff5252', desc:'Draw anything!' },
    { id:'rewards',label:'My Trophies',     emoji:'🏆', color:'#ffb300', desc:'See your rewards!' },
  ],
};

// ═══════════════════════════════════════════════════════════════════
//  REWARDS SYSTEM  ·  in-memory stars · streak · badges · stickers
//  Inspired by Khan Kids / Duolingo motivation loops
// ═══════════════════════════════════════════════════════════════════

const STICKER_POOL = ['🦄','🐉','🚀','🌈','🦖','🐙','🦜','🐠','🦚','🐢','🦋','🐝','🌺','🍭','🎠','🪐','🎨','🧸','🦩','🐬','🦔','🌟','🍓','🐧'];

const BADGES = [
  { id:'first',   icon:'🌟', name:'First Star',     desc:'Earn your first star',           test:s => s.stars >= 1 },
  { id:'rising',  icon:'⭐', name:'Rising Star',     desc:'Collect 25 stars',               test:s => s.stars >= 25 },
  { id:'champ',   icon:'🏆', name:'Star Champion',   desc:'Collect 100 stars',              test:s => s.stars >= 100 },
  { id:'fire',    icon:'🔥', name:'On Fire',         desc:'Finish 3 in a row',              test:s => s.best >= 3 },
  { id:'unstop',  icon:'💪', name:'Unstoppable',     desc:'Finish 7 in a row',              test:s => s.best >= 7 },
  { id:'scholar', icon:'🎓', name:'Scholar',         desc:'Complete 5 activities',          test:s => s.plays >= 5 },
  { id:'brain',   icon:'🧠', name:'Big Brain',       desc:'Complete 15 activities',         test:s => s.plays >= 15 },
  { id:'perfect', icon:'💯', name:'Perfectionist',   desc:'Get a perfect round',            test:s => s.perfects >= 1 },
  { id:'artist',  icon:'🎨', name:'Little Artist',   desc:'Create in the studio',           test:s => s.creations >= 1 },
];

const RewardsContext = React.createContext(null);
function useRewards() { return React.useContext(RewardsContext); }

function RewardsProvider({ children }) {
  const [stars,     setStars]     = useState(0);
  const [streak,    setStreak]    = useState(0);
  const [best,      setBest]      = useState(0);
  const [plays,     setPlays]     = useState(0);
  const [perfects,  setPerfects]  = useState(0);
  const [creations, setCreations] = useState(0);
  const [stickers,  setStickers]  = useState([]);
  const [popup,     setPopup]     = useState(null);

  // Award after finishing a scored activity or a creative session
  const award = useCallback(({ stars: s = 0, perfect = false, creation = false } = {}) => {
    if (s > 0)    setStars(x => x + s);
    if (perfect)  setPerfects(p => p + 1);
    if (creation) setCreations(c => c + 1);
    setPlays(p => p + 1);
    setStreak(st => { const n = st + 1; setBest(b => Math.max(b, n)); return n; });
    setStickers(prev => {
      const pool = STICKER_POOL.filter(e => !prev.includes(e));
      if (!pool.length) return prev;
      return [...prev, pool[Math.floor(Math.random() * pool.length)]];
    });
    const key = Date.now() + Math.random();
    setPopup({ stars: s, perfect, creation, key });
    setTimeout(() => setPopup(p => (p && p.key === key ? null : p)), 2600);
  }, []);

  const stats    = { stars, streak, best, plays, perfects, creations, stickers };
  const unlocked = BADGES.filter(b => b.test(stats)).map(b => b.id);

  return (
    <RewardsContext.Provider value={{ ...stats, unlocked, award }}>
      {children}
      {popup && (
        <div className="ks-reward-pop">
          <span className="ks-reward-pop__icon">{popup.creation ? '🎨' : popup.perfect ? '🏆' : '⭐'}</span>
          <span className="ks-reward-pop__text">
            {popup.creation ? 'Creativity star earned!' : popup.stars > 0 ? `+${popup.stars} star${popup.stars !== 1 ? 's' : ''}!` : 'Nice try!'}
            {popup.perfect && ' Perfect! 💯'}
          </span>
        </div>
      )}
    </RewardsContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MATH DATA
// ═══════════════════════════════════════════════════════════════════

const FOOD_EMOJIS_MATH = ['🍎','🐶','⭐','🎈','🌸','🐱','🍕','🦋','🍦','🥭','🍩','🌙'];

function genMath(ageKey) {
  switch (ageKey) {
    case '3-5': {
      const n = Math.floor(Math.random() * 9) + 1;
      const emoji = FOOD_EMOJIS_MATH[Math.floor(Math.random() * FOOD_EMOJIS_MATH.length)];
      return {
        type: 'count',
        question: `How many ${emoji} do you see? 👀`,
        display: Array.from({ length: n }, () => emoji).join(' '),
        answer: n,
        choices: shuffle([n, ...getWrongNums(n, 1, 10, 3)]),
      };
    }
    case '6-7': {
      const isAdd = Math.random() > 0.35;
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * (isAdd ? 10 : a)) + 1;
      const ans = isAdd ? a + b : a - b;
      return {
        type: 'arithmetic',
        question: `${a} ${isAdd ? '+' : '−'} ${b} = ?`,
        answer: ans,
        choices: shuffle([ans, ...getWrongNums(ans, 0, 25, 3)]),
      };
    }
    case '8-9': {
      const useMul = Math.random() < 0.6;
      if (useMul) {
        const a = Math.floor(Math.random() * 10) + 2;
        const b = Math.floor(Math.random() * 10) + 2;
        return { type: 'arithmetic', question: `${a} × ${b} = ?`, answer: a * b, choices: shuffle([a * b, ...getWrongNums(a * b, 1, 150, 3)]) };
      } else {
        const b = Math.floor(Math.random() * 9) + 2;
        const ans = Math.floor(Math.random() * 12) + 2;
        return { type: 'arithmetic', question: `${b * ans} ÷ ${b} = ?`, answer: ans, choices: shuffle([ans, ...getWrongNums(ans, 1, 20, 3)]) };
      }
    }
    case '10-12': {
      const type = ['mul', 'div', 'frac'][Math.floor(Math.random() * 3)];
      if (type === 'mul') {
        const a = Math.floor(Math.random() * 12) + 2, b = Math.floor(Math.random() * 12) + 2;
        return { type: 'arithmetic', question: `${a} × ${b} = ?`, answer: a * b, choices: shuffle([a * b, ...getWrongNums(a * b, 1, 200, 3)]) };
      } else if (type === 'div') {
        const b = Math.floor(Math.random() * 11) + 2, ans = Math.floor(Math.random() * 12) + 2;
        return { type: 'arithmetic', question: `${b * ans} ÷ ${b} = ?`, answer: ans, choices: shuffle([ans, ...getWrongNums(ans, 1, 20, 3)]) };
      } else {
        const fracs = [
          { q: '½ of 20 = ?', a: '10', ch: ['10', '5', '8', '15'] },
          { q: '¾ of 12 = ?', a: '9', ch: ['9', '6', '8', '3'] },
          { q: '⅓ of 18 = ?', a: '6', ch: ['6', '9', '3', '12'] },
          { q: '¼ of 32 = ?', a: '8', ch: ['8', '4', '16', '6'] },
          { q: '⅔ of 15 = ?', a: '10', ch: ['10', '5', '6', '12'] },
          { q: '½ + ¼ = ?', a: '¾', ch: ['¾', '½', '¼', '1'] },
        ];
        const p = fracs[Math.floor(Math.random() * fracs.length)];
        return { type: 'fraction', question: p.q, answer: p.a, choices: p.ch };
      }
    }
    default: return genMath('6-7');
  }
}

// ═══════════════════════════════════════════════════════════════════
//  LETTER DATA
// ═══════════════════════════════════════════════════════════════════

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const LETTER_WORDS = {
  A:'🍎 Apple', B:'🦋 Butterfly', C:'🐱 Cat', D:'🐶 Dog',
  E:'🐘 Elephant', F:'🐸 Frog', G:'🍇 Grapes', H:'🏠 House',
  I:'🍦 Ice Cream', J:'🌶️ Jalapeño', K:'🪁 Kite', L:'🦁 Lion',
  M:'🌙 Moon', N:'🦏 Narwhal', O:'🐙 Octopus', P:'🦜 Parrot',
  Q:'👸 Queen', R:'🌈 Rainbow', S:'⭐ Star', T:'🐯 Tiger',
  U:'🌂 Umbrella', V:'🚗 Van', W:'🐋 Whale', X:'🪄 Xylophone',
  Y:'🪀 Yo-yo', Z:'🦓 Zebra',
};

function genLetterQ() {
  const letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
  const wrong = shuffle(LETTERS.filter(l => l !== letter)).slice(0, 3);
  return {
    letter,
    word: LETTER_WORDS[letter],
    question: `Which letter does "${LETTER_WORDS[letter].split(' ').slice(1).join(' ')}" start with?`,
    answer: letter,
    choices: shuffle([letter, ...wrong]),
  };
}

// ═══════════════════════════════════════════════════════════════════
//  COLORS & SHAPES DATA
// ═══════════════════════════════════════════════════════════════════

const COLORS = [
  { name: 'Red',    hex: '#e53935', emoji: '🔴' },
  { name: 'Blue',   hex: '#1e88e5', emoji: '🔵' },
  { name: 'Yellow', hex: '#fdd835', emoji: '🟡' },
  { name: 'Green',  hex: '#43a047', emoji: '🟢' },
  { name: 'Orange', hex: '#fb8c00', emoji: '🟠' },
  { name: 'Purple', hex: '#8e24aa', emoji: '🟣' },
  { name: 'Pink',   hex: '#e91e8c', emoji: '🩷' },
];
const SHAPES = [
  { name: 'Circle',   path: <circle cx="60" cy="60" r="50" />,                                            vb: '0 0 120 120' },
  { name: 'Square',   path: <rect x="10" y="10" width="100" height="100" />,                              vb: '0 0 120 120' },
  { name: 'Triangle', path: <polygon points="60,5 115,115 5,115" />,                                      vb: '0 0 120 120' },
  { name: 'Star',     path: <polygon points="60,5 74,40 112,40 82,63 93,100 60,76 27,100 38,63 8,40 46,40" />, vb: '0 0 120 120' },
  { name: 'Diamond',  path: <polygon points="60,5 115,60 60,115 5,60" />,                                 vb: '0 0 120 120' },
  { name: 'Oval',     path: <ellipse cx="60" cy="60" rx="55" ry="35" />,                                  vb: '0 0 120 120' },
];

function genColorShapeQ() {
  const useColor = Math.random() > 0.4;
  if (useColor) {
    const c = COLORS[Math.floor(Math.random() * COLORS.length)];
    const wrong = shuffle(COLORS.filter(x => x.name !== c.name)).slice(0, 3);
    return { type: 'color', display: c, question: 'What color is this? 🎨', answer: c.name, choices: shuffle([c.name, ...wrong.map(x => x.name)]) };
  } else {
    const s = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const wrong = shuffle(SHAPES.filter(x => x.name !== s.name)).slice(0, 3);
    const fillColor = COLORS[Math.floor(Math.random() * COLORS.length)].hex;
    return { type: 'shape', display: s, fillColor, question: 'What shape is this? 🔷', answer: s.name, choices: shuffle([s.name, ...wrong.map(x => x.name)]) };
  }
}

// ═══════════════════════════════════════════════════════════════════
//  SIGHT WORDS
// ═══════════════════════════════════════════════════════════════════

const SIGHT_WORDS = [
  { word: 'the',  sentence: '___ cat sat on a mat.', hint: '🐱' },
  { word: 'and',  sentence: 'I have a cat ___ a dog.', hint: '🐶' },
  { word: 'big',  sentence: 'The elephant is ___.', hint: '🐘' },
  { word: 'run',  sentence: 'I like to ___ fast!', hint: '🏃' },
  { word: 'see',  sentence: 'I can ___ the stars.', hint: '⭐' },
  { word: 'come', sentence: '___ here, little puppy!', hint: '🐾' },
  { word: 'look', sentence: '___ at the rainbow!', hint: '🌈' },
  { word: 'play', sentence: 'We like to ___ outside.', hint: '⚽' },
  { word: 'jump', sentence: 'Frogs can ___ very high.', hint: '🐸' },
  { word: 'blue', sentence: 'The sky is ___.', hint: '🌤️' },
  { word: 'red',  sentence: 'The apple is ___.', hint: '🍎' },
  { word: 'one',  sentence: 'I have ___ cookie.', hint: '🍪' },
  { word: 'said', sentence: 'She ___ hello to me.', hint: '👋' },
  { word: 'from', sentence: 'This gift is ___ you!', hint: '🎁' },
  { word: 'has',  sentence: 'The dog ___ a bone.', hint: '🦴' },
];

// ═══════════════════════════════════════════════════════════════════
//  SKIP COUNTING
// ═══════════════════════════════════════════════════════════════════

function genSkipCountQ() {
  const by = [2, 5, 10][Math.floor(Math.random() * 3)];
  const startMult = Math.floor(Math.random() * 5);
  const start = startMult * by;
  const len = 4 + Math.floor(Math.random() * 3);
  const seq = Array.from({ length: len }, (_, i) => start + i * by);
  const answer = start + len * by;
  let wrongSet = new Set();
  while (wrongSet.size < 3) {
    const delta = (Math.floor(Math.random() * 4) + 1) * by * (Math.random() > 0.5 ? 1 : -1);
    const w = answer + delta;
    if (w > 0 && w !== answer) wrongSet.add(w);
  }
  return { by, seq, answer, choices: shuffle([answer, ...[...wrongSet]]) };
}

// ═══════════════════════════════════════════════════════════════════
//  SPELLING WORDS
// ═══════════════════════════════════════════════════════════════════

const SPELLING_WORDS = [
  { word: 'ELEPHANT',  hint: '🐘 Big animal with a trunk' },
  { word: 'RAINBOW',   hint: '🌈 Colorful arc in the sky' },
  { word: 'BUTTERFLY', hint: '🦋 Insect with pretty wings' },
  { word: 'DOLPHIN',   hint: '🐬 Smart ocean animal' },
  { word: 'MOUNTAIN',  hint: '⛰️ Tall landform' },
  { word: 'KITCHEN',   hint: '🍳 Room where food is made' },
  { word: 'LIBRARY',   hint: '📚 Place with many books' },
  { word: 'UMBRELLA',  hint: '☂️ Keeps you dry in rain' },
  { word: 'OCTOBER',   hint: '🍂 Tenth month of the year' },
  { word: 'TRIANGLE',  hint: '🔺 Shape with 3 sides' },
  { word: 'TELESCOPE', hint: '🔭 Used to see stars' },
  { word: 'ADVENTURE', hint: '🗺️ Exciting journey' },
  { word: 'CHOCOLATE', hint: '🍫 Sweet brown treat' },
  { word: 'GEOGRAPHY', hint: '🌍 Study of Earth' },
];

// ═══════════════════════════════════════════════════════════════════
//  STORIES
// ═══════════════════════════════════════════════════════════════════

const STORIES = [
  {
    title: 'The Dosa Adventure',
    emoji: '🫓',
    minAgeKey: '6-7',
    text: [
      'Priya woke up one morning to the warm smell of crispy dosas cooking in the kitchen. Her grandmother — Nani — was up early making breakfast.',
      '"Nani, can I help?" asked Priya, rubbing her eyes. Her grandmother smiled and handed her a small ladle. "Of course! Come here."',
      '"First," said Nani, "we pour the batter in the center of the pan." She showed Priya how to spread it in slow circles — like the hands of a clock.',
      'Priya tried. Her first dosa came out a little lumpy and uneven. She frowned at it. "It\'s not round!"',
      '"That\'s okay," said Nani with a wink. "My first dosa looked like a map! Practice makes perfect. And see — it still tastes delicious."',
      'Priya took a bite. It was crispy and warm and wonderful. She smiled. That morning, she learned that cooking — just like learning — takes patience, practice, and a lot of love.',
    ],
    questions: [
      { q: 'What was Nani making for breakfast?', a: 'Dosas', choices: ['Dosas', 'Idly', 'Rice', 'Poori'] },
      { q: 'What did Nani hand to Priya?', a: 'A small ladle', choices: ['A small ladle', 'A big bowl', 'An apron', 'A rolling pin'] },
      { q: 'How should you spread the batter?', a: 'In slow circles', choices: ['In slow circles', 'In a square', 'In a straight line', 'As fast as possible'] },
      { q: 'What lesson did Priya learn?', a: 'Practice makes perfect', choices: ['Practice makes perfect', 'Nani is the best cook', 'Dosas are easy to make', 'Cooking is too hard'] },
    ],
  },
  {
    title: 'Ravi and the Mango Tree',
    emoji: '🥭',
    minAgeKey: '6-7',
    text: [
      'Ravi lived in a house with a tall, old mango tree in the backyard. Every summer, he counted the mangoes hanging from its branches and dreamed of sharing them with his whole class.',
      'One afternoon, a sudden storm rolled through. Thunder rumbled, lightning flashed, and wind shook the tree hard. When Ravi looked outside, he saw dozens of mangoes scattered on the ground.',
      '"They\'re ruined!" he cried. But his father knelt beside him and said, "Look closer, Ravi. Touch one."',
      'Ravi picked one up. It was soft, golden, and sweet-smelling. "It\'s ripe!" he gasped. "The storm knocked down all the ripe ones," his father explained. "Nature had a plan all along."',
      'Together, they collected every mango. Ravi\'s mother made mango lassi, mango pickle, and fresh slices with chili powder. Ravi brought some to school the next day.',
      '"Sometimes," his teacher told the class, "what looks like a disaster is really a gift in disguise. We just need to look closer."',
    ],
    questions: [
      { q: 'What did the storm knock down?', a: 'All the ripe mangoes', choices: ['All the ripe mangoes', 'The whole tree', 'Ravi\'s house', 'His school bag'] },
      { q: 'What did Ravi\'s father show him?', a: 'The fallen mangoes were ripe', choices: ['The fallen mangoes were ripe', 'How to climb the tree', 'How to fix the fence', 'A new mango seed'] },
      { q: 'What did Ravi\'s mother NOT make?', a: 'Mango cake', choices: ['Mango cake', 'Mango lassi', 'Mango pickle', 'Mango slices'] },
      { q: 'What does "gift in disguise" mean?', a: 'Something bad that turns out good', choices: ['Something bad that turns out good', 'A birthday present', 'A wrapped mango', 'A storm cloud'] },
    ],
  },
  {
    title: 'The Robot Who Learned to Fail',
    emoji: '🤖',
    minAgeKey: '8-9',
    text: [
      'In a school in the future, there was a small robot named LUMI. LUMI could solve math problems in seconds and memorize entire encyclopedias. But LUMI had never failed at anything — not even once.',
      'One day, the teacher gave the class a creative challenge: "Build something that has never existed before." LUMI scanned its database. Nothing matched. For the first time, LUMI had no answer.',
      '"ERROR," LUMI beeped, confused. Its classmate Aanya patted its arm. "That\'s okay, LUMI. That\'s what creativity feels like at first."',
      'Aanya helped LUMI sketch ideas. Most were terrible. One looked like a broken umbrella. Another like a confused sandwich. LUMI wanted to quit.',
      '"Keep going," said Aanya. "Every great inventor made thousands of bad things first." So LUMI kept building.',
      'On the last day, LUMI presented a tiny solar-powered lantern that could also play music. It wasn\'t perfect. But it was completely original. The class cheered. LUMI learned something no database could teach: the value of making mistakes.',
    ],
    questions: [
      { q: 'What was LUMI\'s challenge?', a: 'Build something that has never existed', choices: ['Build something that has never existed', 'Solve a math problem', 'Memorize the encyclopedia', 'Fix a broken robot'] },
      { q: 'Why was LUMI confused at first?', a: 'It had never failed before', choices: ['It had never failed before', 'Its battery was low', 'The teacher was mean', 'The room was too hot'] },
      { q: 'What did LUMI finally create?', a: 'A solar-powered lantern that played music', choices: ['A solar-powered lantern that played music', 'A flying car', 'A robot pet', 'A time machine'] },
      { q: 'What did LUMI learn?', a: 'The value of making mistakes', choices: ['The value of making mistakes', 'How to memorize faster', 'How to run away from problems', 'Math is more important than art'] },
    ],
  },
  {
    title: 'The Library at the Edge of the Universe',
    emoji: '🌌',
    minAgeKey: '10-12',
    text: [
      'Imagine a library so vast that its shelves stretch past galaxies. Every book ever written — and every book that will ever be written — rests on those infinite shelves. This library has one librarian: an ancient being named Lore.',
      'One day, a young explorer named Zari arrived, breathless. "I\'ve been searching for the answer to life\'s greatest question," she said. Lore smiled. "Which question is that?"',
      '"Why do some people give up and others keep going, even when things are hard?"',
      'Lore led Zari through rows of glowing books. "This section," Lore said, "holds every story of a human who quit." The books were thin. "And this section," — Lore gestured to a towering wall — "holds stories of those who persisted."',
      'Zari pulled out a thick, heavy book. It was full of failures — thousands of them — all belonging to one person. "Who is this?" she asked.',
      '"Someone who kept going," said Lore quietly. "Someone who understood that a stumble forward is still forward." Zari looked at the cover. It had no name — just a mirror. She saw her own face staring back.',
    ],
    questions: [
      { q: 'What is Zari searching for?', a: 'The answer to why some people persist', choices: ['The answer to why some people persist', 'A lost spaceship', 'The biggest book ever', 'A magical map'] },
      { q: 'Why are the "quit" section books thin?', a: 'Those stories ended too soon', choices: ['Those stories ended too soon', 'They are newer books', 'They are easier to read', 'They are in another language'] },
      { q: 'What does the thick book contain?', a: 'Thousands of failures by one person', choices: ['Thousands of failures by one person', 'Maps of the universe', 'Perfect test scores', 'Lists of stars'] },
      { q: 'What does the mirror on the book\'s cover suggest?', a: 'The story is about the reader\'s own potential', choices: ['The story is about the reader\'s own potential', 'Zari is lost', 'Lore wrote the book', 'The library is magical'] },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════
//  MEMORY MATCH DATA
// ═══════════════════════════════════════════════════════════════════

const CARD_POOL = ['🍕','🌮','🍛','🍜','🥙','🍣','🍩','🥗','🍦','🥭','🍎','🐶','🐱','🦋','⭐','🎈','🌸','🚀'];

function buildDeck(pairCount) {
  const pairs = shuffle(CARD_POOL).slice(0, pairCount);
  return shuffle([...pairs, ...pairs].map((emoji, i) => ({ id: i, emoji, flipped: false, matched: false })));
}

// ═══════════════════════════════════════════════════════════════════
//  SCRAMBLE / WORD BUILDER
// ═══════════════════════════════════════════════════════════════════

const SCRAMBLE_BANK = [
  { word: 'MANGO',    hint: '🥭 Sweet tropical fruit' },
  { word: 'TIGER',    hint: '🐯 Big striped cat' },
  { word: 'CLOUD',    hint: '☁️ Fluffy thing in the sky' },
  { word: 'BREAD',    hint: '🍞 Baked fluffy food' },
  { word: 'OCEAN',    hint: '🌊 Giant body of water' },
  { word: 'FLOWER',   hint: '🌸 Pretty plant that blooms' },
  { word: 'SPIDER',   hint: '🕷️ Eight-legged creature' },
  { word: 'BUTTER',   hint: '🧈 Spread on toast' },
  { word: 'SCHOOL',   hint: '🏫 Where kids learn' },
  { word: 'ROCKET',   hint: '🚀 Goes to outer space' },
  { word: 'PLANET',   hint: '🪐 Orbits around a star' },
  { word: 'BRIDGE',   hint: '🌉 Crosses over water' },
  { word: 'JUNGLE',   hint: '🌴 Dense tropical forest' },
  { word: 'PURPLE',   hint: '🟣 A royal color' },
  { word: 'MIRROR',   hint: '🪞 Shows your reflection' },
];

function scrambleWord(word) {
  let arr = word.split('');
  let tries = 0;
  do { arr = shuffle(arr); tries++; } while (arr.join('') === word && tries < 20);
  return arr;
}

// ═══════════════════════════════════════════════════════════════════
//  VOCABULARY
// ═══════════════════════════════════════════════════════════════════

const VOCAB_BANK = [
  { word: 'Curious',      def: 'Eager to know or learn things',      choices: ['Eager to know or learn things', 'Very tired and sleepy', 'Running very fast', 'Making loud noise'] },
  { word: 'Ancient',      def: 'Very old, from long ago',            choices: ['Very old, from long ago', 'Brand new and shiny', 'Extremely large', 'Very small and tiny'] },
  { word: 'Enormous',     def: 'Very large in size',                 choices: ['Very large in size', 'Very small indeed', 'Moving very fast', 'Making loud sounds'] },
  { word: 'Fragile',      def: 'Easily broken or damaged',           choices: ['Easily broken or damaged', 'Extremely strong', 'Very heavy indeed', 'Very old and wise'] },
  { word: 'Vibrant',      def: 'Full of energy and bright color',    choices: ['Full of energy and bright color', 'Dull and grayish', 'Quiet and still', 'Dark and cold'] },
  { word: 'Persevere',    def: 'Keep trying despite difficulties',   choices: ['Keep trying despite difficulties', 'Give up very easily', 'Move very rapidly', 'Sleep very deeply'] },
  { word: 'Transparent',  def: 'Clear, allowing light through',      choices: ['Clear, allowing light through', 'Completely pitch dark', 'Very heavy object', 'Quite noisy thing'] },
  { word: 'Magnificent',  def: 'Impressively grand and beautiful',   choices: ['Impressively grand and beautiful', 'Ordinary and plain', 'Tiny and hidden', 'Very boring indeed'] },
  { word: 'Advocate',     def: 'Someone who supports a cause',       choices: ['Someone who supports a cause', 'Someone who opposes everything', 'A type of fruit', 'A legal document'] },
  { word: 'Diligent',     def: 'Hardworking and careful',            choices: ['Hardworking and careful', 'Lazy and careless', 'Very loud and boisterous', 'Shy and withdrawn'] },
];

// ═══════════════════════════════════════════════════════════════════
//  ROUND & CELEBRATION CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const ROUNDS = 10;
const CONFETTI_SET = ['🎉','⭐','🌟','✨','🎊','💫','🏆','🎈','🍭','🌈','🦋','🎀'];
const PRAISE_MSGS  = [
  '🎉 Amazing work!', '⭐ You\'re a star!', '🔥 On fire!',
  '💡 Super smart!',  '🏆 Champion!',      '🌟 Incredible!',
  '🎊 Fantastic!',    '🚀 Keep soaring!',
];
function getPraise() { return PRAISE_MSGS[Math.floor(Math.random() * PRAISE_MSGS.length)]; }

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { label: '🌅 Good Morning',   sub: 'Ready to learn something new today?' };
  if (h < 17) return { label: '☀️ Good Afternoon', sub: 'Let\'s have some learning fun!' };
  return              { label: '🌙 Good Evening',   sub: 'One last adventure before bed!' };
}

// ═══════════════════════════════════════════════════════════════════
//  HINT / CLUE ENGINE  ·  Duolingo + Khan Academy Kids inspired
//  2-attempt logic: wrong once → contextual hint + retry
//                   wrong twice → answer revealed
// ═══════════════════════════════════════════════════════════════════

function getMathHint(q, ageKey) {
  if (q.type === 'count') {
    const steps = Array.from({ length: Math.min(q.answer, 6) }, (_, i) => i + 1).join(', ');
    return { icon: '👆', text: `Count each one out loud: ${steps}${q.answer > 6 ? ', ...' : ''}. Touch every emoji as you count!` };
  }
  if (q.type === 'fraction') {
    return { icon: '🍕', text: `Bottom number = how many equal slices total. Top number = how many slices you take. Try drawing it!` };
  }
  const qs = q.question;
  const parts = qs.split(' ');
  const a = parseInt(parts[0]), b = parseInt(parts[2]);
  if (qs.includes('+')) return { icon: '🔢', text: `Start at ${a}. Count UP ${b} more! ${a+1}, ${a+2}${b>2?', ...':''}. Use your fingers!` };
  if (qs.includes('\u2212')) return { icon: '🔢', text: `Start at ${a}. Count BACKWARDS ${b} steps! ${a-1}, ${a-2}${b>2?', ...':''}` };
  if (qs.includes('\u00d7')) return { icon: '\ud83d\udca1', text: `${a} groups of ${b}. Try skip-counting by ${Math.min(a,b)}: ${Math.min(a,b)}, ${Math.min(a,b)*2}, ${Math.min(a,b)*3}...` };
  if (qs.includes('\u00f7')) return { icon: '\ud83d\udca1', text: `How many times does ${b} fit into ${a}? Count by ${b}: ${b}, ${b*2}, ${b*3}...` };
  return { icon: '\ud83e\udd14', text: `Look at each choice. Which one seems too big? Which seems too small? Eliminate them!` };
}

function getLetterHint(q) {
  const wordOnly = q.word.split(' ').slice(1).join(' ');
  return { icon: '\ud83d\udd0a', text: `Say "${wordOnly}" very slowly out loud. What is the VERY FIRST sound you hear? That sound is the letter you want!` };
}

function getColorShapeHint(q) {
  if (q.type === 'color') {
    const examples = {
      Red:    'fire trucks \ud83d\ude92, apples \ud83c\udf4e, and stop signs',
      Blue:   'the sky \ud83c\udf24\ufe0f, the ocean \ud83c\udf0a, and blueberries',
      Yellow: 'the sun \u2600\ufe0f, bananas \ud83c\udf4c, and school buses',
      Green:  'grass \ud83c\udf3f, frogs \ud83d\udc38, and broccoli',
      Orange: 'oranges \ud83c\udf4a, carrots \ud83e\udd55, and pumpkins \ud83c\udf83',
      Purple: 'grapes \ud83c\udf47, eggplants, and lavender \ud83d\udc9c',
      Pink:   'flamingos \ud83e\udd69, roses \ud83c\udf38, and cotton candy',
    };
    return { icon: '\ud83c\udfa8', text: `Think of ${examples[q.answer] || 'something colorful around you'} — they are ALL this color!` };
  }
  const facts = {
    Circle:   'NO corners or straight sides — perfectly round! Like a ball \ud83d\udd35 or a coin.',
    Square:   '4 sides ALL the same length + 4 corners. Like a window \ud83e\ude9f!',
    Triangle: '3 sides and 3 corners. Like a pizza slice \ud83c\udf55!',
    Star:     '5 pointy tips sticking outward. Like in the night sky \u2b50!',
    Diamond:  '4 sides like a square, but TILTED sideways. Like a kite \ud83e\ude81!',
    Oval:     'NO corners — like a stretched circle. Like an egg \ud83e\udd5a!',
  };
  return { icon: '\ud83d\udd0d', text: facts[q.answer] || `Count the sides and corners on the shape!` };
}

function getSkipHint(q) {
  const last = q.seq[q.seq.length - 1];
  return { icon: '\ud83d\udd22', text: `Every gap between numbers is exactly ${q.by}. The last number shown is ${last}. So ${last} + ${q.by} = ?` };
}

function getVocabHint(item) {
  const firstWord = item.def.split(' ')[0] + ' ' + item.def.split(' ')[1];
  return { icon: '\ud83d\udcda', text: `The definition starts with "${firstWord}...". Think — does "${item.word}" remind you of any of those answer choices?` };
}

function getSightHint(item, attempt) {
  if (attempt === 1)
    return { icon: '\ud83d\udca1', text: `The word has ${item.word.length} letter${item.word.length > 1 ? 's' : ''}. It starts with "${item.word[0].toUpperCase()}". Read the sentence out loud — which word fits best?` };
  const half = item.word.slice(0, Math.ceil(item.word.length / 2)).toUpperCase();
  return { icon: '\ud83d\udd0d', text: `Almost there! The word begins: "${half}___" and has ${item.word.length} letters total.` };
}

function getSpellHint(item, attempt) {
  if (attempt === 1)
    return { icon: '\ud83d\udca1', text: `First letter: "${item.word[0]}" — The word has ${item.word.length} letters. Say the hint out loud to hear each sound!` };
  const pat = item.word.split('').map((c, i) => i === 0 || i === item.word.length - 1 ? c : '_').join(' ');
  return { icon: '\ud83d\udd0d', text: `First: "${item.word[0]}" · Last: "${item.word[item.word.length-1]}" · Pattern: ${pat}` };
}

function getWordHint(item, attempt) {
  if (attempt === 1)
    return { icon: '\ud83d\udca1', text: `The word starts with "${item.word[0]}". Find that tile and place it first!` };
  return { icon: '\ud83d\udd0d', text: `Start: "${item.word[0]}" then "${item.word[1]}". The word is ${item.word.length} letters long!` };
}

function HintBox({ hint, type = 'clue' }) {
  if (!hint) return null;
  return (
    <div className={`ks-hint-box ks-hint-box--${type}`}>
      <span className="ks-hint-box__icon">{hint.icon}</span>
      <div className="ks-hint-box__body">
        <div className="ks-hint-box__badge">{type === 'clue' ? '\ud83d\udca1 Hint — Try Again!' : '\ud83d\udcda Answer'}</div>
        <div className="ks-hint-box__text">{hint.text}</div>
      </div>
    </div>
  );
}

function Hearts({ attempts, max = 2 }) {
  return (
    <div className="ks-hearts" aria-label={`${max - attempts} attempt${max - attempts !== 1 ? 's' : ''} remaining`}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={`ks-heart${i < attempts ? ' ks-heart--lost' : ''}`}>
          {i < attempts ? '\ud83d\udc94' : '\u2764\ufe0f'}
        </span>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════

function ResultBanner({ correct, answer }) {
  const okRef = React.useRef(['\ud83c\udf89 Brilliant!', '\u2b50 You got it!', '\ud83c� Correct!', '\ud83c\udf8a Amazing!', '\ud83d\ude80 Nailed it!'][Math.floor(Math.random() * 5)]);
  return (
    <div className={`ks-result ${correct ? 'ks-result--ok' : 'ks-result--no'}`}>
      {correct ? okRef.current : `\ud83d\udcda The answer is: ${answer}`}
    </div>
  );
}

  // eslint-disable-next-line no-unused-vars
function ScoreBar({ score, total, label }) {
  return (
    <div className="ks-score-bar">
      <span className="ks-score-bar__label">{label || '⭐ Score'}</span>
      <span className="ks-score-bar__val">{score}/{total}</span>
    </div>
  );
}

function ActivityHeader({ onBack, title, score, total }) {
  return (
    <div className="ks-act-header">
      <button className="ks-back-btn" onClick={onBack}>← Back</button>
      <h2 className="ks-act-title">{title}</h2>
      {total > 0 && <div className="ks-act-score">⭐ {score}/{total}</div>}
    </div>
  );
}

function ProgressBar({ current, total }) {
  const pct = total > 0 ? Math.min((current / total) * 100, 100) : 0;
  return (
    <div className="ks-prog-wrap">
      <div className="ks-prog-header">
        <span className="ks-prog-header__label">
          {current < total ? `Question ${current + 1} of ${total}` : '🎉 Round Complete!'}
        </span>
        <span>{current}/{total}</span>
      </div>
      <div className="ks-prog-track">
        <div className="ks-prog-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CelebrationScreen({ score, total, onPlayAgain, onBack }) {
  const rewards = useRewards();
  const awarded = useRef(false);
  useEffect(() => {
    if (awarded.current || !rewards || total <= 0) return;
    awarded.current = true;
    rewards.award({ stars: score, perfect: score === total });
  }, []); // eslint-disable-line
  const pct   = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = pct >= 90 ? 3 : pct >= 60 ? 2 : 1;
  const trophy = pct >= 90 ? '🏆' : pct >= 60 ? '🌟' : '📖';
  const msg    = pct >= 90 ? 'Perfect Round!' : pct >= 60 ? 'Great Job!' : 'Keep Practicing!';
  return (
    <div className="ks-celebrate">
      <div className="ks-celebrate__confetti" aria-hidden>
        {[...CONFETTI_SET, ...CONFETTI_SET].map((e, i) => (
          <span key={i} style={{ '--ci': i }}>{e}</span>
        ))}
      </div>
      <div className="ks-celebrate__trophy">{trophy}</div>
      <div className="ks-celebrate__title">{msg}</div>
      <div className="ks-celebrate__score">{score} / {total} correct</div>
      <div className="ks-celebrate__stars">
        {Array.from({ length: 3 }, (_, i) => i < stars ? '⭐' : '☆').join('')}
      </div>
      <p className="ks-celebrate__sub">
        {pct}% accuracy · {pct >= 80 ? 'Excellent work! 💪' : 'You\'re improving! 🚀'}
      </p>
      <div className="ks-celebrate__btns">
        <button className="ks-celebrate__again ks-celebrate__again--pink" onClick={onBack}>
          ← Back to Hub
        </button>
        <button className="ks-celebrate__again" onClick={onPlayAgain}>
          Play Again 🔄
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: MATH WHIZ
// ═══════════════════════════════════════════════════════════════════

function MathWhiz({ ageKey, onBack }) {
  const [q, setQ]           = useState(() => genMath(ageKey));
  const [selected, setSel]  = useState(null);
  const [lastWrong, setLW]  = useState(null);
  const [score, setScore]   = useState(0);
  const [total, setTotal]   = useState(0);
  const [shown, setShown]   = useState(false);
  const [streak, setStreak] = useState(0);
  const [attempts, setAtt]  = useState(0);
  const [hint, setHint]     = useState(null);

  const titles = { '3-5': '\ud83d\udd22 Counting Fun', '6-7': '\u2795 Math Whiz', '8-9': '\u2716\ufe0f Math Whiz', '10-12': '\u2797 Math Whiz' };
  const title  = titles[ageKey] || '\u2795 Math Whiz';

  const reset = () => { setQ(genMath(ageKey)); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setStreak(0); setAtt(0); setHint(null); };

  const pick = (c) => {
    if (shown) return;
    const ok = String(c) === String(q.answer);
    if (ok) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1);
      setStreak(s => s + 1); setAtt(0); setHint(null); setLW(null);
    } else {
      const next = attempts + 1;
      setAtt(next); setStreak(0); setLW(c);
      if (next >= 2) {
        setSel(c); setShown(true); setTotal(t => t + 1); setHint(null);
      } else {
        setHint(getMathHint(q, ageKey));
      }
    }
  };

  const next = () => { setQ(genMath(ageKey)); setSel(null); setLW(null); setShown(false); setAtt(0); setHint(null); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title={title} score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title={title} score={score} total={total} />
      {streak >= 3 && <div className="ks-streak"><span className="ks-streak__fire">\ud83d\udd25</span> {streak} in a row! {getPraise()}</div>}
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        {q.type === 'count' && (
          <div className="ks-emoji-count" aria-label={`Count the emojis: ${q.answer}`}>{q.display}</div>
        )}
        <div className="ks-question">{q.question}</div>
        <div className="ks-choices ks-choices--2">
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${
                shown
                  ? String(c) === String(q.answer) ? 'ks-choice--ok'
                    : String(c) === String(selected) ? 'ks-choice--no' : 'ks-choice--dim'
                  : lastWrong && String(c) === String(lastWrong) ? 'ks-choice--no'
                  : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {hint && !shown && <HintBox hint={hint} type="clue" />}
        {shown && <ResultBanner correct={String(selected) === String(q.answer)} answer={q.answer} />}
        {shown && <button className="ks-next" onClick={next}>Next Question \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: LETTER LAND
// ═══════════════════════════════════════════════════════════════════

function LetterLand({ onBack }) {
  const [q, setQ]          = useState(genLetterQ);
  const [selected, setSel] = useState(null);
  const [lastWrong, setLW] = useState(null);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);
  const [shown, setShown]  = useState(false);
  const [attempts, setAtt] = useState(0);
  const [hint, setHint]    = useState(null);

  const reset = () => { setQ(genLetterQ()); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setAtt(0); setHint(null); };

  const pick = (c) => {
    if (shown) return;
    if (c === q.answer) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null); setLW(null);
    } else {
      const next = attempts + 1; setAtt(next); setLW(c);
      if (next >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); setHint(null); }
      else setHint(getLetterHint(q));
    }
  };

  const next = () => { setQ(genLetterQ()); setSel(null); setLW(null); setShown(false); setAtt(0); setHint(null); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="\ud83d\udd24 Letter Land" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83d\udd24 Letter Land" score={score} total={total} />
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        <div className="ks-letter-bubble">{q.letter}</div>
        <div className="ks-letter-clue">{q.word}</div>
        <div className="ks-question">{q.question}</div>
        <div className="ks-choices ks-choices--2">
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ks-choice--letter ${
                shown ? c === q.answer ? 'ks-choice--ok' : c === selected ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && c === lastWrong ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {hint && !shown && <HintBox hint={hint} type="clue" />}
        {shown && <ResultBanner correct={selected === q.answer} answer={q.answer} />}
        {shown && <button className="ks-next" onClick={next}>Next Letter \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: COLORS & SHAPES
// ═══════════════════════════════════════════════════════════════════

function ColorsShapes({ onBack }) {
  const [q, setQ]          = useState(genColorShapeQ);
  const [selected, setSel] = useState(null);
  const [lastWrong, setLW] = useState(null);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);
  const [shown, setShown]  = useState(false);
  const [attempts, setAtt] = useState(0);
  const [hint, setHint]    = useState(null);

  const reset = () => { setQ(genColorShapeQ()); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setAtt(0); setHint(null); };

  const pick = (c) => {
    if (shown) return;
    if (c === q.answer) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null); setLW(null);
    } else {
      const next = attempts + 1; setAtt(next); setLW(c);
      if (next >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); setHint(null); }
      else setHint(getColorShapeHint(q));
    }
  };

  const next = () => { setQ(genColorShapeQ()); setSel(null); setLW(null); setShown(false); setAtt(0); setHint(null); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="\ud83c\udfa8 Colors & Shapes" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83c\udfa8 Colors & Shapes" score={score} total={total} />
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        {q.type === 'color' ? (
          <div className="ks-color-swatch" style={{ background: q.display.hex }}><span>{q.display.emoji}</span></div>
        ) : (
          <div className="ks-shape-display">
            <svg viewBox={q.display.vb} width="130" height="130">
              {React.cloneElement(q.display.path, { fill: q.fillColor })}
            </svg>
          </div>
        )}
        <div className="ks-question">{q.question}</div>
        <div className="ks-choices ks-choices--2">
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${
                shown ? c === q.answer ? 'ks-choice--ok' : c === selected ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && c === lastWrong ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {hint && !shown && <HintBox hint={hint} type="clue" />}
        {shown && <ResultBanner correct={selected === q.answer} answer={q.answer} />}
        {shown && <button className="ks-next" onClick={next}>Next \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: SIGHT WORDS
// ═══════════════════════════════════════════════════════════════════

function SightWords({ onBack }) {
  const [pool]            = useState(() => shuffle(SIGHT_WORDS));
  const [idx, setIdx]     = useState(0);
  const [input, setInput] = useState('');
  const [checked, setChk] = useState(false);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [attempts, setAtt]= useState(0);
  const [hint, setHint]   = useState(null);
  const inputRef          = useRef(null);

  const item = pool[idx % pool.length];

  const check = () => {
    if (!input.trim() || checked) return;
    const ok = input.trim().toLowerCase() === item.word;
    if (ok) {
      setChk(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null);
    } else {
      const next = attempts + 1; setAtt(next);
      if (next >= 2) {
        setChk(true); setTotal(t => t + 1); setHint(null);
      } else {
        setHint(getSightHint(item, next));
        setInput('');
        setTimeout(() => inputRef.current?.focus(), 80);
      }
    }
  };

  const next = () => {
    setIdx(i => i + 1); setInput(''); setChk(false); setAtt(0); setHint(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83d\udcd6 Sight Words" score={score} total={total} />
      <div className="ks-card">
        {attempts > 0 && !checked && <Hearts attempts={attempts} />}
        <div className="ks-sight-hint">{item.hint}</div>
        <div className="ks-sight-sentence">{item.sentence}</div>
        <p className="ks-subtext">Type the missing word to complete the sentence!</p>
        {hint && !checked && <HintBox hint={hint} type="clue" />}
        <input ref={inputRef} className="ks-input" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && check()}
          placeholder={attempts > 0 ? `Hint: starts with "${item.word[0].toUpperCase()}"…` : 'type the word\u2026'}
          disabled={checked} autoFocus />
        {!checked && <button className="ks-next" onClick={check}>Check \u2713</button>}
        {checked && <ResultBanner correct={input.trim().toLowerCase() === item.word} answer={item.word} />}
        {checked && <button className="ks-next" onClick={next}>Next Word \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: SKIP COUNTING
// ═══════════════════════════════════════════════════════════════════

function SkipCounting({ onBack }) {
  const [q, setQ]          = useState(genSkipCountQ);
  const [selected, setSel] = useState(null);
  const [lastWrong, setLW] = useState(null);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);
  const [shown, setShown]  = useState(false);
  const [attempts, setAtt] = useState(0);
  const [hint, setHint]    = useState(null);

  const reset = () => { setQ(genSkipCountQ()); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setAtt(0); setHint(null); };

  const pick = (c) => {
    if (shown) return;
    if (c === q.answer) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null); setLW(null);
    } else {
      const next = attempts + 1; setAtt(next); setLW(c);
      if (next >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); setHint(null); }
      else setHint(getSkipHint(q));
    }
  };

  const next = () => { setQ(genSkipCountQ()); setSel(null); setLW(null); setShown(false); setAtt(0); setHint(null); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="\ud83d\udd22 Skip Counting" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83d\udd22 Skip Counting" score={score} total={total} />
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        <div className="ks-question">Count by <strong>{q.by}s</strong> \u2014 what comes next?</div>
        <div className="ks-sequence">
          {q.seq.map((n, i) => <span key={i} className="ks-seq-chip">{n}</span>)}
          <span className="ks-seq-chip ks-seq-chip--blank">?</span>
        </div>
        <div className="ks-choices ks-choices--2">
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${
                shown ? c === q.answer ? 'ks-choice--ok' : c === selected ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && c === lastWrong ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {hint && !shown && <HintBox hint={hint} type="clue" />}
        {shown && <ResultBanner correct={selected === q.answer} answer={q.answer} />}
        {shown && <button className="ks-next" onClick={next}>Next \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: SPELLING BEE
// ═══════════════════════════════════════════════════════════════════

function SpellingBee({ onBack }) {
  const [pool]            = useState(() => shuffle(SPELLING_WORDS));
  const [idx, setIdx]     = useState(0);
  const [input, setInput] = useState('');
  const [checked, setChk] = useState(false);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [attempts, setAtt]= useState(0);
  const [hint, setHint]   = useState(null);
  const inputRef          = useRef(null);

  const item = pool[idx % pool.length];
  const emojiPart   = item.hint.split(' ')[0];
  const hintText    = item.hint.split(' ').slice(1).join(' ');

  // Build blank slot classes: revealed letters show in green/yellow on hint
  const getBlankClass = (i) => {
    if (checked) return input[i] ? 'ks-blank--filled' : '';
    if (attempts >= 1 && i === 0) return 'ks-blank--hint';        // first letter hinted
    if (attempts >= 2 && (i === 0 || i === item.word.length - 1)) return 'ks-blank--hint'; // first+last hinted
    return input[i] ? 'ks-blank--filled' : '';
  };
  const getBlankChar = (i) => {
    if (checked) return item.word[i];
    if (attempts >= 1 && i === 0) return item.word[0]; // reveal first letter
    if (attempts >= 2 && i === item.word.length - 1) return item.word[i]; // reveal last letter
    return input[i] || '';
  };

  const check = () => {
    if (!input.trim() || checked) return;
    const ok = input.trim().toUpperCase() === item.word;
    if (ok) {
      setChk(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null);
    } else {
      const next = attempts + 1; setAtt(next);
      if (next >= 3) {
        setChk(true); setTotal(t => t + 1); setHint(null);
      } else {
        setHint(getSpellHint(item, next));
        setInput('');
        setTimeout(() => inputRef.current?.focus(), 80);
      }
    }
  };

  const next = () => {
    setIdx(i => i + 1); setInput(''); setChk(false); setAtt(0); setHint(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83d\udc1d Spelling Bee" score={score} total={total} />
      <div className="ks-card">
        {attempts > 0 && !checked && <Hearts attempts={attempts} max={3} />}
        <div className="ks-spell-emoji">{emojiPart}</div>
        <div className="ks-question">{hintText}</div>
        <p className="ks-subtext">Spell the word \u2014 {item.word.length} letters!</p>
        {hint && !checked && <HintBox hint={hint} type="clue" />}
        <div className="ks-blank-slots">
          {item.word.split('').map((_, i) => (
            <span key={i} className={`ks-blank ${getBlankClass(i)}`}>
              {getBlankChar(i)}
            </span>
          ))}
        </div>
        <input ref={inputRef} className="ks-input"
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
          onKeyDown={e => e.key === 'Enter' && check()}
          placeholder={attempts >= 1 ? `Starts with "${item.word[0]}"\u2026` : 'TYPE HERE\u2026'}
          maxLength={item.word.length + 3}
          disabled={checked} autoFocus />
        {!checked && <button className="ks-next" onClick={check}>Check \u2713</button>}
        {checked && <ResultBanner correct={input.trim() === item.word} answer={item.word} />}
        {checked && <button className="ks-next" onClick={next}>Next Word \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: STORY CORNER
// ═══════════════════════════════════════════════════════════════════

function StoryCorner({ ageKey, onBack }) {
  const available = STORIES.filter((s, i) => {
    const order = { '6-7': 0, '8-9': 1, '10-12': 2 };
    const minOrder = order[s.minAgeKey] ?? 0;
    const curOrder = order[ageKey] ?? 0;
    return curOrder >= minOrder;
  });

  const [storyIdx, setStoryIdx] = useState(0);
  const [page,    setPage]      = useState(0);
  const [answers, setAnswers]   = useState({});
  const [done,    setDone]      = useState(false);

  const story       = available[storyIdx % available.length];
  const totalPages  = story.text.length;
  const isReading   = page < totalPages;
  const quizScore   = done ? story.questions.reduce((a, q, i) => a + (answers[i] === q.a ? 1 : 0), 0) : 0;
  const allAnswered = Object.keys(answers).length === story.questions.length;

  const nextStory = () => {
    setStoryIdx(i => i + 1);
    setPage(0); setAnswers({}); setDone(false);
  };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="📚 Story Corner"
        score={done ? quizScore : 0} total={done ? story.questions.length : 0} />
      <div className="ks-card ks-card--story">
        <div className="ks-story-title">{story.emoji} {story.title}</div>

        {isReading ? (
          <>
            {/* progress dots */}
            <div className="ks-story-dots">
              {story.text.map((_, i) => (
                <span key={i} className={`ks-story-dot ${i === page ? 'active' : i < page ? 'done' : ''}`} />
              ))}
            </div>
            <div className="ks-story-text">"{story.text[page]}"</div>
            <div className="ks-story-nav">
              {page > 0 && <button className="ks-nav-btn" onClick={() => setPage(p => p - 1)}>← Prev</button>}
              <span className="ks-pg">{page + 1} / {totalPages}</span>
              {page < totalPages - 1
                ? <button className="ks-nav-btn ks-nav-btn--primary" onClick={() => setPage(p => p + 1)}>Next →</button>
                : <button className="ks-nav-btn ks-nav-btn--primary" onClick={() => setPage(totalPages)}>Quiz Time! 📝</button>}
            </div>
          </>
        ) : !done ? (
          <>
            <p className="ks-quiz-label">📝 Comprehension — answer all 4 questions!</p>
            {story.questions.map((q, qi) => (
              <div key={qi} className="ks-story-q">
                <div className="ks-story-q__num">Q{qi + 1}</div>
                <div className="ks-story-q__text">{q.q}</div>
                <div className="ks-choices ks-choices--col">
                  {q.choices.map((c, ci) => (
                    <button key={ci}
                      className={`ks-choice ks-choice--sm ${answers[qi] === c ? 'ks-choice--selected' : ''}`}
                      onClick={() => setAnswers(a => ({ ...a, [qi]: c }))}
                    >{c}</button>
                  ))}
                </div>
              </div>
            ))}
            {allAnswered && (
              <button className="ks-next" style={{ marginTop: 20 }} onClick={() => setDone(true)}>
                See Results 🎯
              </button>
            )}
          </>
        ) : (
          <div className="ks-story-results">
            <div className="ks-story-trophy">
              {quizScore === story.questions.length ? '🏆' : quizScore >= story.questions.length / 2 ? '🌟' : '📖'}
            </div>
            <div className="ks-story-verdict">
              {quizScore === story.questions.length ? 'Perfect Score!' : quizScore >= story.questions.length / 2 ? 'Well done!' : 'Keep reading!'}
            </div>
            <div className="ks-story-stars">
              {Array.from({ length: story.questions.length }, (_, i) => (
                <span key={i}>{i < quizScore ? '⭐' : '☆'}</span>
              ))}
            </div>
            {story.questions.map((q, i) => (
              <div key={i} className={`ks-ans-row ${answers[i] === q.a ? 'ks-ans-row--ok' : 'ks-ans-row--no'}`}>
                {answers[i] === q.a ? '✅' : '❌'} {q.q}
                <div className="ks-ans-correct">✔ {q.a}</div>
              </div>
            ))}
            <button className="ks-next" style={{ marginTop: 20 }} onClick={nextStory}>
              {storyIdx + 1 < available.length ? 'Next Story →' : 'Read Again 🔄'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: MEMORY MATCH
// ═══════════════════════════════════════════════════════════════════

function MemoryMatch({ ageKey, onBack }) {
  const pairs = ageKey === '3-5' ? 4 : ageKey === '6-7' ? 6 : 8;
  const cols  = ageKey === '3-5' ? 4 : 4;

  const [cards, setCards]   = useState(() => buildDeck(pairs));
  const [flipped, setFlip]  = useState([]);
  const [moves,  setMoves]  = useState(0);
  const [won,    setWon]    = useState(false);
  const lock = useRef(false);
  const rewards = useRewards();
  const awarded = useRef(false);

  const flip = useCallback((id) => {
    if (lock.current) return;
    setCards(cs => {
      const c = cs.find(x => x.id === id);
      if (!c || c.flipped || c.matched) return cs;
      return cs.map(x => x.id === id ? { ...x, flipped: true } : x);
    });
    setFlip(f => [...f, id]);
  }, []);

  useEffect(() => {
    if (flipped.length < 2) return;
    lock.current = true;
    setMoves(m => m + 1);
    const [a, b] = flipped;
    const ca = cards.find(x => x.id === a);
    const cb = cards.find(x => x.id === b);
    if (ca && cb && ca.emoji === cb.emoji) {
      setCards(cs => cs.map(x => (x.id === a || x.id === b) ? { ...x, matched: true } : x));
      setFlip([]);
      lock.current = false;
    } else {
      setTimeout(() => {
        setCards(cs => cs.map(x => (x.id === a || x.id === b) ? { ...x, flipped: false } : x));
        setFlip([]);
        lock.current = false;
      }, 900);
    }
  }, [flipped]); // eslint-disable-line

  useEffect(() => {
    if (cards.length > 0 && cards.every(c => c.matched)) setWon(true);
  }, [cards]);

  const stars = moves <= pairs * 2 ? 3 : moves <= pairs * 3 ? 2 : 1;

  useEffect(() => {
    if (won && !awarded.current && rewards) {
      awarded.current = true;
      rewards.award({ stars, perfect: stars === 3 });
    }
  }, [won]); // eslint-disable-line

  const reset = () => {
    setCards(buildDeck(pairs)); setFlip([]); setMoves(0); setWon(false); lock.current = false; awarded.current = false;
  };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🧩 Memory Match" score={0} total={0} />
      <div className="ks-mem-stats">
        <span className="ks-mem-stat">🎯 Moves: {moves}</span>
        <span className="ks-mem-stat ks-mem-stat--matched">✅ {cards.filter(c => c.matched).length / 2}/{pairs}</span>
      </div>
      {won ? (
        <div className="ks-win-card">
          <div className="ks-win-trophy">🏆</div>
          <div className="ks-win-msg">You won in {moves} moves!</div>
          <div className="ks-win-stars">{Array.from({ length: 3 }, (_, i) => <span key={i}>{i < stars ? '⭐' : '☆'}</span>)}</div>
          <p className="ks-win-submsg">
            {stars === 3 ? 'Perfect memory! 🏅' : stars === 2 ? 'Great job! 🌟' : 'Good effort! 💪'}
          </p>
          <button className="ks-next" onClick={reset}>Play Again 🔄</button>
        </div>
      ) : (
        <div className="ks-memory-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {cards.map(card => (
            <div className="ks-mem-card-wrap" key={card.id}>
              <button
                className={`ks-mem-card${card.flipped || card.matched ? ' ks-mem-card--face' : ''}${card.matched ? ' ks-mem-card--matched' : ''}`}
                onClick={() => !card.matched && flip(card.id)}
                disabled={card.matched}
              >
                <span className="ks-mem-card__back">❓</span>
                <span className="ks-mem-card__front">{card.emoji}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: WORD BUILDER
// ═══════════════════════════════════════════════════════════════════

function WordBuilder({ onBack }) {
  const [pool]             = useState(() => shuffle(SCRAMBLE_BANK));
  const [idx, setIdx]      = useState(0);
  const [tiles, setTiles]  = useState(() => scrambleWord(shuffle(SCRAMBLE_BANK)[0].word));
  const [typed,  setTyped] = useState([]);
  const [checked, setChk]  = useState(false);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);

  const item = pool[idx % pool.length];

  useEffect(() => { setTiles(scrambleWord(item.word)); setTyped([]); setChk(false); }, [idx]); // eslint-disable-line

  const tapTile = (ti) => {
    if (checked) return;
    setTiles(ts => ts.map((t, i) => i === ti ? { ...t, used: true } : t));
    setTyped(tp => [...tp, { letter: tiles[ti].letter ?? tiles[ti], tileIdx: ti }]);
  };

  const removeLast = () => {
    if (checked || typed.length === 0) return;
    const last = typed[typed.length - 1];
    setTiles(ts => ts.map((t, i) => i === last.tileIdx ? { ...t, used: false } : t));
    setTyped(tp => tp.slice(0, -1));
  };

  const [wbAttempts, setWbAtt] = useState(0);
  const [wbHint, setWbHint]    = useState(null);

  const check = () => {
    if (!typed.length) return;
    const formed = typed.map(t => t.letter).join('');
    const ok = formed === item.word;
    if (ok) {
      setChk(true); setTotal(t => t + 1); setScore(s => s + 1); setWbAtt(0); setWbHint(null);
    } else {
      const next = wbAttempts + 1; setWbAtt(next);
      if (next >= 2) {
        setChk(true); setTotal(t => t + 1); setWbHint(null);
      } else {
        setWbHint(getWordHint(item, next));
        setTyped([]);
        setTiles(ts => ts.map(t => typeof t === 'string' ? { letter: t, used: false } : { ...t, used: false }));
      }
    }
  };

  const next = () => { setIdx(i => i + 1); setWbAtt(0); setWbHint(null); };

  // normalise tiles
  const normTiles = tiles.map(t => typeof t === 'string' ? { letter: t, used: false } : t);
  const formed = typed.map(t => t.letter).join('');
  const correct = formed === item.word;
  // Find tile to highlight for hint (first tile with the first letter of the word)
  const hintTileIdx = wbHint && !checked ? normTiles.findIndex(t => !t.used && t.letter === item.word[typed.length]) : -1;

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🔠 Word Builder" score={score} total={total} />
      <div className="ks-card">
        <div className="ks-spell-emoji">{item.hint.split(' ')[0]}</div>
        <div className="ks-question">{item.hint.split(' ').slice(1).join(' ')}</div>
        {/* Answer slots */}
        <div className="ks-blank-slots">
          {item.word.split('').map((_, i) => (
            <span key={i} className={`ks-blank ${typed[i] ? 'ks-blank--filled' : ''} ${checked ? correct ? 'ks-blank--ok' : 'ks-blank--no' : ''}`}>
              {typed[i]?.letter || ''}
            </span>
          ))}
        </div>
        {/* Hint box */}
        {wbHint && !checked && <HintBox hint={wbHint} type="clue" />}
        {wbAttempts > 0 && !checked && <Hearts attempts={wbAttempts} />}
        {/* Letter tiles */}
        {!checked && (
          <div className="ks-tiles">
            {normTiles.map((t, i) => (
              <button key={i}
                className={`ks-tile-btn ${t.used ? 'ks-tile-btn--used' : ''} ${!t.used && i === hintTileIdx ? 'ks-tile-btn--hint' : ''}`}
                onClick={() => !t.used && tapTile(i)}
                disabled={t.used}
              >{t.letter}</button>
            ))}
          </div>
        )}
        {/* Controls */}
        {!checked && (
          <div className="ks-word-controls">
            <button className="ks-word-ctrl" onClick={removeLast} disabled={!typed.length}>⌫ Undo</button>
            <button className="ks-next" onClick={check} disabled={typed.length === 0}>Check ✓</button>
          </div>
        )}
        {checked && <ResultBanner correct={correct} answer={item.word} />}
        {checked && <button className="ks-next" onClick={next}>Next Word →</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: VOCABULARY
// ═══════════════════════════════════════════════════════════════════

function Vocabulary({ onBack }) {
  const [pool]             = useState(() => shuffle(VOCAB_BANK));
  const [idx, setIdx]      = useState(0);
  const [selected, setSel] = useState(null);
  const [lastWrong, setLW] = useState(null);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);
  const [shown, setShown]  = useState(false);
  const [attempts, setAtt] = useState(0);
  const [hint, setHint]    = useState(null);

  const item = pool[idx % pool.length];

  const reset = () => { setIdx(0); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setAtt(0); setHint(null); };

  const pick = (c) => {
    if (shown) return;
    if (c === item.def) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setAtt(0); setHint(null); setLW(null);
    } else {
      const next = attempts + 1; setAtt(next); setLW(c);
      if (next >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); setHint(null); }
      else setHint(getVocabHint(item));
    }
  };

  const next = () => { setIdx(i => i + 1); setSel(null); setLW(null); setShown(false); setAtt(0); setHint(null); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="\ud83d\udcdd Vocabulary" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="\ud83d\udcdd Vocabulary" score={score} total={total} />
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        <div className="ks-vocab-word">{item.word}</div>
        <div className="ks-question">What does this word mean?</div>
        <div className="ks-choices ks-choices--col">
          {item.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${
                shown ? c === item.def ? 'ks-choice--ok' : c === selected ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && c === lastWrong ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {hint && !shown && <HintBox hint={hint} type="clue" />}
        {shown && <ResultBanner correct={selected === item.def} answer={item.def} />}
        {shown && <button className="ks-next" onClick={next}>Next Word \u2192</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  KNOWLEDGE DATA  ·  Science / Geography with fun facts
// ═══════════════════════════════════════════════════════════════════

const SCIENCE_BANK = [
  { q:'Which animal is the biggest on Earth?', choices:['Blue whale','Elephant','Giraffe','Shark'], a:'Blue whale', fact:'A blue whale’s heart is as big as a small car! 🐋' },
  { q:'What do bees make?', choices:['Honey','Milk','Bread','Juice'], a:'Honey', fact:'Bees visit about 2 million flowers to make one jar of honey! 🐝' },
  { q:'Which planet do we live on?', choices:['Earth','Mars','Jupiter','Venus'], a:'Earth', fact:'Earth is the only planet we know with living things. 🌍' },
  { q:'What gives us light in the daytime?', choices:['The Sun','The Moon','Stars','Lamps'], a:'The Sun', fact:'The Sun is a giant ball of burning gas — our closest star! ☀️' },
  { q:'How many legs does a spider have?', choices:['8','6','4','10'], a:'8', fact:'Spiders have 8 legs — insects only have 6! 🕷️' },
  { q:'What do plants need to grow?', choices:['Sunlight & water','Only candy','Only darkness','Only sand'], a:'Sunlight & water', fact:'Plants make their own food from sunlight — it’s called photosynthesis! 🌱' },
  { q:'Which animal can change its color?', choices:['Chameleon','Dog','Horse','Cow'], a:'Chameleon', fact:'Chameleons change color to hide and to show their mood! 🦎' },
  { q:'What is the fastest land animal?', choices:['Cheetah','Lion','Elephant','Turtle'], a:'Cheetah', fact:'A cheetah can run as fast as a car on the highway! 🐆' },
  { q:'What do we call frozen water?', choices:['Ice','Steam','Mud','Sand'], a:'Ice', fact:'Water turns to ice when it gets very cold — below 0°C! ❄️' },
  { q:'Which part of your body pumps blood?', choices:['Heart','Brain','Lungs','Stomach'], a:'Heart', fact:'Your heart beats about 100,000 times every day! ❤️' },
  { q:'What do caterpillars turn into?', choices:['Butterflies','Birds','Bees','Frogs'], a:'Butterflies', fact:'A caterpillar makes a cocoon, then becomes a butterfly! 🦋' },
  { q:'Which gas do we breathe to live?', choices:['Oxygen','Helium','Smoke','Steam'], a:'Oxygen', fact:'Trees make the oxygen we breathe — thank a tree! 🌳' },
  { q:'What is the closest star to Earth?', choices:['The Sun','The Moon','Mars','Pluto'], a:'The Sun', fact:'Sunlight takes 8 minutes to reach us — super fast! 🚀' },
  { q:'Which animal lives in water and breathes with gills?', choices:['Fish','Cat','Bird','Rabbit'], a:'Fish', fact:'Gills let fish take oxygen straight from the water! 🐠' },
  { q:'What makes a rainbow?', choices:['Sunlight & rain','Snow','Wind','Thunder'], a:'Sunlight & rain', fact:'Light bends through raindrops to make 7 colors! 🌈' },
  { q:'How many bones does an adult human have?', choices:['206','100','50','500'], a:'206', fact:'Babies are born with about 300 bones — some fuse together! 🦴' },
  { q:'Which dinosaur was a giant meat-eater?', choices:['T. rex','Stegosaurus','Triceratops','Brachiosaurus'], a:'T. rex', fact:'T. rex teeth were as long as bananas! 🦖' },
  { q:'What pulls things down to the ground?', choices:['Gravity','Wind','Magnets','Light'], a:'Gravity', fact:'Gravity keeps your feet on the ground and the Moon orbiting Earth! 🌑' },
];

const GEO_BANK = [
  { flag:'🇯🇵', q:'Which country’s flag is this?', choices:['Japan','China','Korea','Thailand'], a:'Japan', fact:'Japan is called the Land of the Rising Sun! ☀️' },
  { flag:'🇫🇷', q:'Which country’s flag is this?', choices:['France','Italy','Russia','Spain'], a:'France', fact:'The Eiffel Tower in France is 330 metres tall! 🗼' },
  { flag:'🇧🇷', q:'Which country’s flag is this?', choices:['Brazil','Argentina','Mexico','Peru'], a:'Brazil', fact:'Brazil is home to the huge Amazon rainforest! 🌿' },
  { flag:'🇮🇳', q:'Which country’s flag is this?', choices:['India','Pakistan','Nepal','Sri Lanka'], a:'India', fact:'India is the most populated country in the world! 🪔' },
  { flag:'🇨🇦', q:'Which country’s flag is this?', choices:['Canada','USA','UK','Australia'], a:'Canada', fact:'Canada’s flag has a red maple leaf! 🍁' },
  { q:'What is the capital of France?', choices:['Paris','London','Rome','Berlin'], a:'Paris', fact:'Paris is known as the City of Light! 💡' },
  { q:'What is the capital of Japan?', choices:['Tokyo','Osaka','Kyoto','Seoul'], a:'Tokyo', fact:'Tokyo is one of the biggest cities on Earth! 🏙️' },
  { q:'What is the capital of India?', choices:['New Delhi','Mumbai','Chennai','Kolkata'], a:'New Delhi', fact:'India is famous for the beautiful Taj Mahal! 🕌' },
  { q:'What is the capital of the USA?', choices:['Washington, D.C.','New York','Los Angeles','Chicago'], a:'Washington, D.C.', fact:'The White House is in Washington, D.C.! 🏛️' },
  { q:'Which is the largest ocean?', choices:['Pacific','Atlantic','Indian','Arctic'], a:'Pacific', fact:'The Pacific Ocean is bigger than all the land combined! 🌊' },
  { q:'Which continent is the coldest?', choices:['Antarctica','Africa','Asia','Europe'], a:'Antarctica', fact:'Antarctica is covered in ice and home to penguins! 🐧' },
  { q:'Which is the tallest mountain on Earth?', choices:['Mount Everest','Kilimanjaro','Mount Fuji','Alps'], a:'Mount Everest', fact:'Mount Everest is almost 9 km tall — higher than planes fly low! ⛰️' },
  { q:'Which country is shaped like a boot?', choices:['Italy','Spain','Greece','Egypt'], a:'Italy', fact:'Italy is famous for pizza and pasta! 🍕' },
  { q:'Which is the largest desert?', choices:['Sahara','Gobi','Mojave','Thar'], a:'Sahara', fact:'The Sahara in Africa is almost as big as the whole USA! 🏜️' },
  { q:'How many continents are there?', choices:['7','5','10','3'], a:'7', fact:'The seven continents include Asia, the biggest one! 🗺️' },
  { q:'Which river is the longest?', choices:['Nile','Amazon','Ganges','Thames'], a:'Nile', fact:'The Nile in Africa flows over 6,600 km! 🛶' },
];

function buildKnowQuestion(item) {
  return { ...item, choices: shuffle(item.choices) };
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY: KNOWLEDGE QUIZ  (Science · Geography)
// ═══════════════════════════════════════════════════════════════════

function KnowledgeQuiz({ title, bank, onBack }) {
  const [pool]            = useState(() => shuffle(bank));
  const [idx, setIdx]     = useState(0);
  const [q, setQ]         = useState(() => buildKnowQuestion(shuffle(bank)[0]));
  const [selected, setSel]= useState(null);
  const [lastWrong, setLW]= useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [shown, setShown] = useState(false);
  const [streak, setStreak] = useState(0);
  const [attempts, setAtt]= useState(0);

  // keep q in sync with idx
  useEffect(() => { setQ(buildKnowQuestion(pool[idx % pool.length])); }, [idx, pool]);

  const reset = () => { setIdx(0); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setStreak(0); setAtt(0); };

  const pick = (c) => {
    if (shown) return;
    if (c === q.a) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setStreak(s => s + 1); setAtt(0); setLW(null);
    } else {
      const n = attempts + 1; setAtt(n); setStreak(0); setLW(c);
      if (n >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); }
    }
  };

  const next = () => { setIdx(i => i + 1); setSel(null); setLW(null); setShown(false); setAtt(0); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title={title} score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title={title} score={score} total={total} />
      {streak >= 3 && <div className="ks-streak"><span className="ks-streak__fire">🔥</span> {streak} in a row! {getPraise()}</div>}
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        {q.flag && <div className="ks-flag">{q.flag}</div>}
        <div className="ks-question">{q.q}</div>
        <div className="ks-choices ks-choices--col">
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${
                shown ? c === q.a ? 'ks-choice--ok' : c === selected ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && c === lastWrong ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {attempts === 1 && !shown && <HintBox hint={{ icon:'🤔', text:'Not quite — look carefully and try once more!' }} type="clue" />}
        {shown && <ResultBanner correct={selected === q.a} answer={q.a} />}
        {shown && q.fact && (
          <div className="ks-fact-box">
            <span className="ks-fact-box__icon">💡</span>
            <div><div className="ks-fact-box__badge">Did you know?</div><div className="ks-fact-box__text">{q.fact}</div></div>
          </div>
        )}
        {shown && <button className="ks-next" onClick={next}>Next →</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PATTERN DATA + ACTIVITY: PATTERN PUZZLE
// ═══════════════════════════════════════════════════════════════════

const PATTERN_SETS = [
  ['🔴','🔵'], ['🟡','🟢'], ['⭐','🌙'], ['🍎','🍌'],
  ['🐶','🐱'], ['❤️','💙','💚'], ['🚗','✈️','🚲'], ['🔺','⬜','🔻'],
];
const PATTERN_DISTRACTORS = ['🔴','🔵','🟡','🟢','⭐','🌙','🍎','🍌','🐶','🐱','❤️','💙','💚','🚗','✈️','🚲','🔺','🔻','🌺','🐝'];

function genPattern(ageKey) {
  const useNum = (ageKey === '8-9' || ageKey === '10-12') && Math.random() < 0.5;
  if (useNum) {
    const step  = [2, 3, 5, 10][Math.floor(Math.random() * 4)];
    const start = Math.floor(Math.random() * 5) + 1;
    const seq   = [start, start + step, start + 2 * step, start + 3 * step];
    const answer = start + 4 * step;
    const wrong = getWrongNums(answer, Math.max(0, answer - 12), answer + 12, 3);
    return { kind:'num', display:[...seq.map(String), '?'], answer:String(answer), choices: shuffle([String(answer), ...wrong.map(String)]) };
  }
  const set  = PATTERN_SETS[Math.floor(Math.random() * PATTERN_SETS.length)];
  const reps = ageKey === '3-5' ? 2 : 3;
  const len  = reps * set.length;
  const full = Array.from({ length: len }, (_, i) => set[i % set.length]);
  const answer = full[full.length - 1];
  const display = [...full.slice(0, -1), '?'];
  const distractPool = PATTERN_DISTRACTORS.filter(e => e !== answer);
  const wrong = shuffle([...new Set([...set.filter(e => e !== answer), ...distractPool])]).slice(0, 3);
  return { kind:'emoji', display, answer, choices: shuffle([answer, ...wrong]) };
}

function PatternPuzzle({ ageKey, onBack }) {
  const [q, setQ]          = useState(() => genPattern(ageKey));
  const [selected, setSel] = useState(null);
  const [lastWrong, setLW] = useState(null);
  const [score, setScore]  = useState(0);
  const [total, setTotal]  = useState(0);
  const [shown, setShown]  = useState(false);
  const [streak, setStreak]= useState(0);
  const [attempts, setAtt] = useState(0);

  const reset = () => { setQ(genPattern(ageKey)); setSel(null); setLW(null); setScore(0); setTotal(0); setShown(false); setStreak(0); setAtt(0); };

  const pick = (c) => {
    if (shown) return;
    if (String(c) === String(q.answer)) {
      setSel(c); setShown(true); setTotal(t => t + 1); setScore(s => s + 1); setStreak(s => s + 1); setAtt(0); setLW(null);
    } else {
      const n = attempts + 1; setAtt(n); setStreak(0); setLW(c);
      if (n >= 2) { setSel(c); setShown(true); setTotal(t => t + 1); }
    }
  };

  const next = () => { setQ(genPattern(ageKey)); setSel(null); setLW(null); setShown(false); setAtt(0); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="🔮 What Comes Next" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🔮 What Comes Next" score={score} total={total} />
      {streak >= 3 && <div className="ks-streak"><span className="ks-streak__fire">🔥</span> {streak} in a row! {getPraise()}</div>}
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        {attempts > 0 && !shown && <Hearts attempts={attempts} />}
        <div className="ks-question">What comes next? 🤔</div>
        <div className={`ks-pattern-strip ${q.kind === 'num' ? 'ks-pattern-strip--num' : ''}`}>
          {q.display.map((d, i) => (
            <span key={i} className={`ks-pattern-cell ${d === '?' ? 'ks-pattern-cell--q' : ''}`}>{d}</span>
          ))}
        </div>
        <div className={`ks-choices ${q.kind === 'num' ? 'ks-choices--2' : 'ks-choices--2'}`}>
          {q.choices.map((c, i) => (
            <button key={i}
              className={`ks-choice ${q.kind === 'emoji' ? 'ks-choice--letter' : ''} ${
                shown ? String(c) === String(q.answer) ? 'ks-choice--ok' : String(c) === String(selected) ? 'ks-choice--no' : 'ks-choice--dim'
                : lastWrong && String(c) === String(lastWrong) ? 'ks-choice--no' : ''}`}
              onClick={() => pick(c)}
            >{c}</button>
          ))}
        </div>
        {attempts === 1 && !shown && <HintBox hint={{ icon:'🔍', text:'Look at how the pattern repeats, then try again!' }} type="clue" />}
        {shown && <ResultBanner correct={String(selected) === String(q.answer)} answer={q.answer} />}
        {shown && <button className="ks-next" onClick={next}>Next Pattern →</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ODD ONE OUT  ·  focus / attention training
// ═══════════════════════════════════════════════════════════════════

const ODD_DISTINCT = ['🍎','🚀','🐶','⭐','🎈','🌈','🍕','🦋','🐱','🌙','🎨','🐢','🍓','🐧'];
const ODD_PAIRS = [
  ['🐶','🐺'], ['😺','😸'], ['🌝','🌚'], ['🔵','🟣'],
  ['🍊','🍋'], ['⭐','🌟'], ['🐢','🐸'], ['🍎','🍅'],
  ['🌸','🌺'], ['🐝','🦋'], ['🚗','🚙'], ['😀','😄'],
  ['🐰','🐭'], ['🔴','🟠'], ['🦊','🐱'], ['🌲','🎄'],
];

function genOdd(ageKey) {
  const size = ageKey === '3-5' ? 9 : ageKey === '6-7' ? 16 : ageKey === '8-9' ? 20 : 25;
  let base, odd;
  if (ageKey === '3-5') {
    const s = shuffle(ODD_DISTINCT); base = s[0]; odd = s[1];
  } else {
    const p = ODD_PAIRS[Math.floor(Math.random() * ODD_PAIRS.length)];
    const flip = Math.random() < 0.5;
    base = flip ? p[0] : p[1];
    odd  = flip ? p[1] : p[0];
  }
  return { size, base, odd, oddIndex: Math.floor(Math.random() * size) };
}

function OddOneOut({ ageKey, onBack }) {
  const cols = ageKey === '3-5' ? 3 : ageKey === '6-7' ? 4 : 5;
  const [q, setQ]         = useState(() => genOdd(ageKey));
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [picked, setPicked] = useState(null);
  const [shown, setShown] = useState(false);

  const reset = () => { setQ(genOdd(ageKey)); setScore(0); setTotal(0); setPicked(null); setShown(false); };

  const tap = (i) => {
    if (shown) return;
    setPicked(i); setShown(true); setTotal(t => t + 1);
    if (i === q.oddIndex) setScore(s => s + 1);
  };

  const next = () => { setQ(genOdd(ageKey)); setPicked(null); setShown(false); };

  if (total >= ROUNDS) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="🔍 Odd One Out" score={score} total={total} />
        <CelebrationScreen score={score} total={total} onPlayAgain={reset} onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🔍 Odd One Out" score={score} total={total} />
      <div className="ks-card">
        <ProgressBar current={total} total={ROUNDS} />
        <div className="ks-question">Tap the one that is different! 👀</div>
        <div className="ks-odd-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: q.size }, (_, i) => {
            const isOdd = i === q.oddIndex;
            const cls = shown ? (isOdd ? 'ks-odd-cell--found' : i === picked ? 'ks-odd-cell--wrong' : '') : '';
            return (
              <button key={i} className={`ks-odd-cell ${cls}`} onClick={() => tap(i)} disabled={shown}>
                {isOdd ? q.odd : q.base}
              </button>
            );
          })}
        </div>
        {shown && (
          <ResultBanner correct={picked === q.oddIndex} answer={`${q.odd} was the odd one`} />
        )}
        {shown && <button className="ks-next" onClick={next}>Next Round →</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MEMORY LIGHTS  ·  Simon-style growing sequence memory
// ═══════════════════════════════════════════════════════════════════

const SIMON_PADS = [
  { c:'#ff5252', cl:'#ffb3b3', e:'🍎' },
  { c:'#42a5f5', cl:'#b3dbff', e:'🟦' },
  { c:'#66bb6a', cl:'#c2f0c2', e:'🍀' },
  { c:'#ffca28', cl:'#ffe9a8', e:'⭐' },
];

function SimonSays({ onBack }) {
  const [seq, setSeq]     = useState([]);
  const [step, setStep]   = useState(0);     // index user is on
  const [phase, setPhase] = useState('idle'); // idle | show | input | over
  const [active, setActive] = useState(null);
  const [level, setLevel] = useState(0);
  const [best, setBest]   = useState(0);
  const rewards = useRewards();
  const timers  = useRef([]);
  const awarded = useRef(false);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const playSeq = useCallback((sequence) => {
    setPhase('show'); setActive(null);
    clearTimers();
    sequence.forEach((pad, i) => {
      timers.current.push(setTimeout(() => setActive(pad), 650 * i + 350));
      timers.current.push(setTimeout(() => setActive(null), 650 * i + 350 + 400));
    });
    timers.current.push(setTimeout(() => { setPhase('input'); setStep(0); }, 650 * sequence.length + 400));
  }, []);

  const start = () => {
    awarded.current = false;
    const first = [Math.floor(Math.random() * 4)];
    setSeq(first); setLevel(1); setPhase('show'); setStep(0);
    playSeq(first);
  };

  const tap = (pad) => {
    if (phase !== 'input') return;
    setActive(pad);
    timers.current.push(setTimeout(() => setActive(null), 200));
    if (pad === seq[step]) {
      if (step + 1 === seq.length) {
        // round complete → lock input, then grow the sequence
        setPhase('show');
        const grown = [...seq, Math.floor(Math.random() * 4)];
        timers.current.push(setTimeout(() => { setSeq(grown); setLevel(grown.length); playSeq(grown); }, 600));
      } else {
        setStep(s => s + 1);
      }
    } else {
      setPhase('over');
      setBest(b => Math.max(b, level));
      if (!awarded.current && rewards) {
        awarded.current = true;
        const stars = level >= 7 ? 3 : level >= 4 ? 2 : 1;
        rewards.award({ stars, perfect: level >= 8 });
      }
    }
  };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🎵 Memory Lights" score={0} total={0} />
      <div className="ks-mem-stats">
        <span className="ks-mem-stat">🎯 Level: {level}</span>
        <span className="ks-mem-stat ks-mem-stat--matched">🏅 Best: {best}</span>
      </div>
      <div className="ks-card">
        {phase === 'idle' && (
          <>
            <div className="ks-simon-intro">🎵</div>
            <div className="ks-question">Watch the lights, then repeat them in order!</div>
            <button className="ks-next" onClick={start}>Start ▶️</button>
          </>
        )}
        {(phase === 'show' || phase === 'input') && (
          <>
            <div className="ks-simon-status">
              {phase === 'show' ? '👀 Watch carefully…' : '👆 Your turn! Repeat it'}
            </div>
            <div className="ks-simon-board">
              {SIMON_PADS.map((p, i) => (
                <button key={i}
                  className={`ks-simon-pad ${active === i ? 'ks-simon-pad--on' : ''}`}
                  style={{ '--pad':p.c, '--padlight':p.cl }}
                  onClick={() => tap(i)}
                  disabled={phase !== 'input'}
                >{p.e}</button>
              ))}
            </div>
          </>
        )}
        {phase === 'over' && (
          <div className="ks-win-card" style={{ boxShadow:'none', padding:'8px 0' }}>
            <div className="ks-win-trophy">{level >= 7 ? '🏆' : level >= 4 ? '🌟' : '🧠'}</div>
            <div className="ks-win-msg">You reached level {level}!</div>
            <p className="ks-win-submsg">{level >= 7 ? 'Incredible memory! 🏅' : level >= 4 ? 'Great focus! 🌟' : 'Keep practicing your memory! 💪'}</p>
            <button className="ks-next" onClick={start}>Play Again 🔄</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  DOODLE PAD  ·  free-draw creativity canvas
// ═══════════════════════════════════════════════════════════════════

const DOODLE_COLORS = ['#1a1a2e','#e53935','#ff9500','#ffca28','#43a047','#42a5f5','#6c63ff','#ff6b9d','#795548','#ffffff'];

function DoodlePad({ onBack }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const last      = useRef({ x:0, y:0 });
  const [color, setColor] = useState('#6c63ff');
  const [size, setSize]   = useState(8);
  const [erase, setErase] = useState(false);
  const [dirty, setDirty] = useState(false);
  const rewards = useRewards();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect  = canvas.getBoundingClientRect();
    canvas.width  = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const moveDraw  = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.strokeStyle = erase ? '#ffffff' : color;
    ctx.lineWidth = erase ? size * 2.5 : size;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!dirty) setDirty(true);
  };
  const endDraw = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setDirty(false);
  };

  const finish = () => {
    if (dirty && rewards) rewards.award({ creation: true });
    onBack();
  };

  return (
    <div className="ks-activity">
      <div className="ks-act-header">
        <button className="ks-back-btn" onClick={onBack}>← Back</button>
        <h2 className="ks-act-title">🖍️ Doodle Pad</h2>
        <div />
      </div>
      <div className="ks-card ks-card--doodle">
        <div className="ks-doodle-tools">
          {DOODLE_COLORS.map(c => (
            <button key={c}
              className={`ks-color-dot ${color === c && !erase ? 'ks-color-dot--on' : ''}`}
              style={{ background:c, borderColor: c === '#ffffff' ? '#ddd' : c }}
              onClick={() => { setColor(c); setErase(false); }}
              aria-label={`Color ${c}`}
            />
          ))}
          <button className={`ks-tool-btn ${erase ? 'ks-tool-btn--on' : ''}`} onClick={() => setErase(e => !e)}>🧽 Eraser</button>
        </div>
        <div className="ks-brush-row">
          <span className="ks-brush-label">Brush</span>
          {[4, 8, 16, 28].map(s => (
            <button key={s}
              className={`ks-brush-btn ${size === s ? 'ks-brush-btn--on' : ''}`}
              onClick={() => setSize(s)}
            ><span style={{ width:s, height:s }} /></button>
          ))}
        </div>
        <canvas
          ref={canvasRef}
          className="ks-doodle-canvas"
          onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
        />
        <div className="ks-doodle-actions">
          <button className="ks-back-btn" onClick={clear}>🗑️ Clear</button>
          <button className="ks-next" style={{ marginTop:0 }} onClick={finish}>I’m done! ⭐</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  STORY MAKER  ·  fill-the-blanks silly stories (mad-libs)
// ═══════════════════════════════════════════════════════════════════

const STORY_CHOICES = {
  animal:  ['dragon', 'penguin', 'elephant', 'kitten', 'dinosaur', 'unicorn'],
  place:   ['the moon', 'a jungle', 'school', 'the beach', 'a castle', 'outer space'],
  object:  ['a giant pizza', 'a magic hat', 'a robot', 'a rainbow', 'a treasure box', 'a balloon'],
  exclaim: ['Wowzers', 'Yippee', 'Oh my', 'Hooray', 'Ta-da', 'Yikes'],
  action:  ['danced', 'zoomed', 'giggled', 'bounced', 'tip-toed', 'twirled'],
};
const STORY_TEMPLATES = [
  { title:'The Silly Adventure', slots:['animal','place','object','exclaim','action'],
    build:(p)=>`One sunny day, a brave ${p.animal} set off to ${p.place}. There it found ${p.object} and shouted, “${p.exclaim}!” Then the happy ${p.animal} ${p.action} all the way home for dinner. The End! 🌟` },
  { title:'The Magic Day', slots:['animal','object','place','action','exclaim'],
    build:(p)=>`Once upon a time, a curious ${p.animal} found ${p.object} hidden at ${p.place}. “${p.exclaim}!” it cried, and ${p.action} with joy. Everyone cheered for the clever little ${p.animal}! 🎉` },
];
const SLOT_LABELS = { animal:'an animal', place:'a place', object:'a thing', exclaim:'a fun word', action:'an action' };

function StoryBuilder({ onBack }) {
  const [tpl]    = useState(() => STORY_TEMPLATES[Math.floor(Math.random() * STORY_TEMPLATES.length)]);
  const [picks, setPicks] = useState({});
  const [done, setDone]   = useState(false);
  const rewards = useRewards();

  const allFilled = tpl.slots.every(s => picks[s]);

  const choose = (slot, val) => setPicks(p => ({ ...p, [slot]: val }));

  const make = () => {
    setDone(true);
    if (rewards) rewards.award({ creation: true });
  };

  const restart = () => { setPicks({}); setDone(false); };

  if (done) {
    return (
      <div className="ks-activity">
        <ActivityHeader onBack={onBack} title="✨ Story Maker" score={0} total={0} />
        <div className="ks-card">
          <div className="ks-story-title">📖 {tpl.title}</div>
          <p className="ks-story-text">{tpl.build(picks)}</p>
          <button className="ks-next" onClick={restart}>Make Another 🔄</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="✨ Story Maker" score={0} total={0} />
      <div className="ks-card ks-card--story">
        <div className="ks-question">Pick your words to build a silly story! 📝</div>
        {tpl.slots.map(slot => (
          <div key={slot} className="ks-story-slot">
            <div className="ks-story-slot__label">Choose {SLOT_LABELS[slot]}:</div>
            <div className="ks-story-chips">
              {STORY_CHOICES[slot].map(val => (
                <button key={val}
                  className={`ks-chip ${picks[slot] === val ? 'ks-chip--on' : ''}`}
                  onClick={() => choose(slot, val)}
                >{val}</button>
              ))}
            </div>
          </div>
        ))}
        <button className="ks-next" disabled={!allFilled} onClick={make}>
          {allFilled ? 'Make my story! ✨' : 'Pick all the words ⬆️'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  REWARD ROOM  ·  trophies · badges · sticker book
// ═══════════════════════════════════════════════════════════════════

function RewardRoom({ onBack }) {
  const r = useRewards();
  const stats = { stars:r.stars, best:r.best, plays:r.plays, perfects:r.perfects, creations:r.creations };

  return (
    <div className="ks-activity">
      <ActivityHeader onBack={onBack} title="🏆 My Trophies" score={0} total={0} />
      <div className="ks-reward-summary">
        <div className="ks-reward-stat"><span className="ks-reward-stat__big">⭐ {r.stars}</span><span className="ks-reward-stat__lbl">Stars</span></div>
        <div className="ks-reward-stat"><span className="ks-reward-stat__big">🔥 {r.best}</span><span className="ks-reward-stat__lbl">Best Streak</span></div>
        <div className="ks-reward-stat"><span className="ks-reward-stat__big">🎮 {r.plays}</span><span className="ks-reward-stat__lbl">Activities</span></div>
      </div>

      <div className="ks-card" style={{ alignItems:'stretch' }}>
        <div className="ks-reward-heading">🎖️ Badges</div>
        <div className="ks-badge-grid">
          {BADGES.map(b => {
            const got = b.test(stats);
            return (
              <div key={b.id} className={`ks-badge ${got ? 'ks-badge--got' : 'ks-badge--locked'}`}>
                <span className="ks-badge__icon">{got ? b.icon : '🔒'}</span>
                <span className="ks-badge__name">{b.name}</span>
                <span className="ks-badge__desc">{got ? b.desc : '???'}</span>
              </div>
            );
          })}
        </div>

        <div className="ks-reward-heading" style={{ marginTop:18 }}>✨ Sticker Book ({r.stickers.length})</div>
        {r.stickers.length === 0 ? (
          <p className="ks-reward-empty">Finish activities to collect stickers! 🎁</p>
        ) : (
          <div className="ks-sticker-grid">
            {r.stickers.map((s, i) => <span key={i} className="ks-sticker">{s}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

function RewardsBar({ onOpen }) {
  const r = useRewards();
  return (
    <button className="ks-rewards-bar" onClick={onOpen}>
      <span className="ks-rewards-bar__item">⭐ <b>{r.stars}</b></span>
      <span className="ks-rewards-bar__item">🔥 <b>{r.best}</b></span>
      <span className="ks-rewards-bar__item">🎖️ <b>{r.unlocked.length}</b></span>
      <span className="ks-rewards-bar__cta">My Trophies →</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  AGE SELECTOR SCREEN
// ═══════════════════════════════════════════════════════════════════

function AgeSelector({ onSelect }) {
  const [age, setAge] = useState(7);

  return (
    <div className="ks-age-screen">
      {/* Floating stars bg */}
      <div className="ks-stars-bg" aria-hidden>
        {Array.from({ length: 18 }, (_, i) => (
          <span key={i} className="ks-star-float" style={{ '--i': i, left: `${(i * 7 + 3) % 97}%`, animationDelay: `${(i * 0.4) % 3}s` }}>
            {['⭐','🌟','✨','💫'][i % 4]}
          </span>
        ))}
      </div>

      <div className="ks-age-inner">
        <div className="ks-age-brand">🌟 Kids Corner 🌟</div>
        <h1 className="ks-age-headline">How old are you?</h1>
        <p className="ks-age-sub">We'll pick the perfect activities just for you!</p>

        {/* Number wheel */}
        <div className="ks-age-picker">
          <button className="ks-age-btn ks-age-btn--minus"
            onClick={() => setAge(a => Math.max(3, a - 1))}>−</button>
          <div className="ks-age-num">
            <span key={age} className="ks-age-num__val">{age}</span>
          </div>
          <button className="ks-age-btn ks-age-btn--plus"
            onClick={() => setAge(a => Math.min(12, a + 1))}>+</button>
        </div>

        {/* Age group quick select */}
        <div className="ks-age-groups">
          {AGE_GROUPS.map(g => {
            const active = getAgeKey(age) === g.key;
            return (
              <button key={g.key}
                className={`ks-age-group-btn ${active ? 'ks-age-group-btn--active' : ''}`}
                style={{ '--gc': g.color, '--gl': g.light }}
                onClick={() => setAge(parseInt(g.key.split('-')[0]))}
              >
                <span className="ks-age-group-btn__emoji">{g.emoji}</span>
                <span className="ks-age-group-btn__label">{g.label}</span>
                <span className="ks-age-group-btn__grade">{g.grade}</span>
              </button>
            );
          })}
        </div>

        <button className="ks-start-btn" onClick={() => onSelect(age)}>
          Let's Go! 🚀
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ACTIVITY HUB
// ═══════════════════════════════════════════════════════════════════

function ActivityHub({ ageKey, onSelect, onChangeAge }) {
  const group    = AGE_GROUPS.find(g => g.key === ageKey);
  const actList  = ACTIVITY_MAP[ageKey] || ACTIVITY_MAP['6-7'];
  const greeting = getGreeting();

  return (
    <div className="ks-hub" style={{ '--hub-color': group.color, '--hub-light': group.light, '--hub-dark': group.dark }}>

      {/* Greeting Banner */}
      <div className="ks-hub-banner" data-emoji={group.emoji}>
        <div className="ks-hub-banner__time">{greeting.label}</div>
        <div className="ks-hub-banner__main">{group.emoji} {group.label} · {group.grade}</div>
        <div className="ks-hub-banner__sub">{greeting.sub}</div>
        <div className="ks-hub-banner__pills">
          <span className="ks-hub-banner__pill">🎯 {actList.length} Activities</span>
          <span className="ks-hub-banner__pill">📚 Learn &amp; Play</span>
          <span className="ks-hub-banner__pill">⭐ Free Forever</span>
        </div>
      </div>

      {/* Rewards bar */}
      <RewardsBar onOpen={() => onSelect('rewards')} />

      {/* Change age */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="ks-change-age" onClick={onChangeAge}>Change Age ✏️</button>
      </div>

      {/* Activity grid */}
      <div className="ks-hub-grid">
        {actList.map(act => (
          <button key={act.id}
            className="ks-act-tile"
            style={{ '--tc': act.color }}
            onClick={() => onSelect(act.id)}
          >
            <span className="ks-act-tile__emoji">{act.emoji}</span>
            <span className="ks-act-tile__label">{act.label}</span>
            <span className="ks-act-tile__desc">{act.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  GAME WRAPPER (embeds DesiBitesDash inline)
// ═══════════════════════════════════════════════════════════════════

function GameWrapper({ onBack }) {
  return (
    <div className="ks-activity">
      <div className="ks-act-header">
        <button className="ks-back-btn" onClick={onBack}>← Back</button>
        <h2 className="ks-act-title">🎮 Desi Bites Dash</h2>
        <div />
      </div>
      <div className="ks-game-embed">
        <DesiBitesDash />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ROOT EXPORT
// ═══════════════════════════════════════════════════════════════════

function KidsSectionInner() {
  const [age,      setAge]      = useState(null);
  const [activity, setActivity] = useState(null);

  const ageKey = age !== null ? getAgeKey(age) : null;
  const back = () => setActivity(null);

  const renderActivity = () => {
    switch (activity) {
      case 'game':       return <GameWrapper    onBack={back} />;
      case 'math':       return <MathWhiz       ageKey={ageKey} onBack={back} />;
      case 'count':      return <MathWhiz       ageKey="3-5"    onBack={back} />;
      case 'abc':        return <LetterLand     onBack={back} />;
      case 'colors':     return <ColorsShapes   onBack={back} />;
      case 'words':      return <SightWords     onBack={back} />;
      case 'skip':       return <SkipCounting   onBack={back} />;
      case 'spelling':   return <SpellingBee    onBack={back} />;
      case 'story':      return <StoryCorner    ageKey={ageKey} onBack={back} />;
      case 'memory':     return <MemoryMatch    ageKey={ageKey} onBack={back} />;
      case 'word':       return <WordBuilder    onBack={back} />;
      case 'vocab':      return <Vocabulary     onBack={back} />;
      case 'simon':      return <SimonSays      onBack={back} />;
      case 'pattern':    return <PatternPuzzle  ageKey={ageKey} onBack={back} />;
      case 'oddone':     return <OddOneOut      ageKey={ageKey} onBack={back} />;
      case 'science':    return <KnowledgeQuiz  title="🔬 Fun Facts"      bank={SCIENCE_BANK} onBack={back} />;
      case 'geo':        return <KnowledgeQuiz  title="🌍 World Explorer" bank={GEO_BANK}     onBack={back} />;
      case 'doodle':     return <DoodlePad      onBack={back} />;
      case 'storymaker': return <StoryBuilder   onBack={back} />;
      case 'rewards':    return <RewardRoom     onBack={back} />;
      default:           return null;
    }
  };

  if (activity) return <div className="ks-root">{renderActivity()}</div>;
  if (age === null) return <div className="ks-root"><AgeSelector onSelect={setAge} /></div>;

  return (
    <div className="ks-root">
      <ActivityHub ageKey={ageKey} onSelect={setActivity} onChangeAge={() => { setAge(null); setActivity(null); }} />
    </div>
  );
}

export default function KidsSection() {
  return (
    <RewardsProvider>
      <KidsSectionInner />
    </RewardsProvider>
  );
}
